// React GUI — glassmorphism (Tailwind). Main menu, loading screen, in-game HUD.
import React, { useState, useEffect, useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import htm from 'htm';
import { store, MAP_SIZES } from '../core/store.js';
import { bus } from '../core/bus.js';
import { SPECIES } from '../systems/animalCatalog.js';
import { bloodlinePct, saleValue } from '../systems/animalRules.js';
import { ANIMAL_THUMBS } from '../systems/animals.js';
import { AMENITIES } from '../systems/amenityCatalog.js';
import { AMENITY_THUMBS } from '../systems/amenities.js';
import { LOAN_OPTIONS, MARKETING, STAFF, PERMIT_TIERS, PROJECTS } from '../systems/economy.js';
import { listSlots, SLOT_COUNT } from '../systems/save.js';
import { TROPHIES } from '../systems/missions.js';
import { PERKS } from '../systems/gameplay.js';
import { LEGENDS, LEGEND_HEX } from '../systems/overlayStyle.js';
import { LENSES, TONE } from '../systems/overlays.js';
import { PROPS } from '../systems/props.js';
import { RESEARCH } from '../systems/research.js';

// Animal shop categories
const PREDATORS = new Set(['lion', 'tiger', 'cheetah', 'wolf', 'bear', 'polarbear', 'crocodile', 'fox']);
const BIRDS_PETS = new Set(['flamingo', 'ostrich', 'emu', 'goose', 'penguin', 'seal', 'shiba', 'husky']);
function animalCat(key) {
  if (PREDATORS.has(key)) return 'predators';
  if (BIRDS_PETS.has(key) || (SPECIES[key] && SPECIES[key].group === 'pets')) return 'other';
  return 'prey';
}

const html = htm.bind(React.createElement);

function useGameState() {
  return useSyncExternalStore(store.subscribe, store.get);
}

const SEASON_ICONS = { spring: '🌱', summer: '☀️', autumn: '🍂', winter: '❄️' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// CC-BY attributions for downloaded 3D models (shown in the in-game Credits screen + credits.txt).
const CREDITS = [
  'Lion by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/3XAJojWxSWz)',
  'Sea lion by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/45HRvXYpvUG)',
  'Elephant by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/a27MA0rXyyj)',
  'Tiger by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/5A3w06FXUup)',
  'Gazelle by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/bPYIQ_XQbrj)',
  'Rhinoceros by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/7XutktqrTj_)',
  'Giraffe by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/80w8kwQU0QH)',
  'Penguin by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/fBXvsC6pe_V)',
  'Koala by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/fzCu8FM0HfB)',
  'Black bear by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/56ym_pyVnel)',
  'Panda by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/2T6A0o4Kq2h)',
  'Gorilla by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/bmfQ1j9CeO2)',
  'Kangaroo by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/7uiVArqEC80)',
  'Cheetah by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/5y59KqZXxWf)',
  'Bison by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/9sTrha-TxdS)',
  'Camel by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/cyuwV4fg0B1)',
  'Antelope by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/dC2-G-IF8rz)',
  'Boar by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/57fSWum6F1P)',
  'Ape by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/2wRk-94IPnU)',
  'Flamingo by Хмара Сергей [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/5hUIvLVFqet)',
  'Zebra by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/8Ut7mhb-aqK)',
  'Ostrich by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/1LtIviy4khn)',
  'Emu by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/dVhJSddeK67)',
  'Alligator by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/dtH7YQkl5qm)',
  'Goose by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/9wn3If7Qgb4)',
  'Hippopotamus by Poly by Google [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/eau2X1phgjJ)',
];

function MainMenu() {
  const [zooName, setZooName] = useState('');
  const [mapSize, setMapSize] = useState('medium');
  const [kidsMode, setKidsMode] = useState(true);
  const [showCredits, setShowCredits] = useState(false);

  return html`
    <div className="zmenu-wrap" style=${{ backgroundImage: "radial-gradient(ellipse at 50% 30%, rgba(10,20,26,0.32), rgba(8,13,18,0.86) 75%), url('uploads/menu-bg.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="zmenu">
        <div className="zmenu-hero">
          <div className="zmenu-logo"><img src="uploads/arkadia-logo.png" alt="Arkadia: Wilder Pedigree" style=${{ width: '210px', height: 'auto', display: 'block' }} /></div>
          <div><div className="zmenu-big">Build your<br/>dream zoo</div><div className="zmenu-tag">Breed rare animals, delight your guests and grow the world's greatest park.</div></div>
          <div className="zmenu-emoji">🦁🐘🦩🐧</div>
          <button type="button" onClick=${() => setShowCredits(true)} className="zmenu-foot zmenu-foot-btn">3D models CC-BY · Poly Pizza</button>
        </div>
        <div className="zmenu-form">
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <div className="zmenu-l">Zoo name</div>
            <input className="zmenu-input" placeholder="e.g. Wildwood Park" value=${zooName} onChange=${(e) => setZooName(e.target.value)} />
          </div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <div className="zmenu-l">Map size</div>
            <div className="zmenu-seg">
              ${Object.entries(MAP_SIZES).map(([key, cfg]) => html`<button key=${key} type="button" onClick=${() => setMapSize(key)} className=${'zmenu-o' + (mapSize === key ? ' on' : '')}>${cfg.label}<span className="c">${cfg.cells}×${cfg.cells}</span></button>`)}
            </div>
          </div>
          <button className="zmenu-primary" disabled=${!zooName.trim()} onClick=${() => bus.emit('game:new', { zooName: zooName.trim(), mapSize, kidsMode: true })}>
            <span className="zmenu-primary-ic">▶</span>
            <span className="zmenu-primary-txt"><span className="zmenu-primary-t">New Game</span><span className="zmenu-primary-s">Start building your zoo</span></span>
          </button>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '7px', marginTop: '2px' }}>
            <div className="zmenu-l">Load game</div>
            ${listSlots().map((meta, i) => html`
              <button key=${i} type="button" disabled=${!meta} onClick=${() => bus.emit('game:load', i)} className="zmenu-slot">
                <span style=${{ fontSize: '17px' }}>${meta ? '💾' : '➕'}</span>
                <span style=${{ minWidth: 0 }}><span className="sn">${i === 3 ? 'Autosave' : (meta ? meta.zooName : 'Slot ' + (i + 1))}</span> <span className="sm">${meta ? (i === 3 ? '— ' + meta.zooName + ' · Y' + meta.year : '— Y' + meta.year + ' · $' + meta.money.toLocaleString('en-US')) : '— empty'}</span></span>
                ${meta ? html`<span className="go">Load</span>` : ''}
              </button>`)}
          </div>
        </div>
      </div>
      ${showCredits && html`
        <div className="absolute inset-0 flex items-center justify-center" style=${{ background: 'rgba(6,10,14,0.75)' }} onClick=${() => setShowCredits(false)}>
          <div className="glass-dark rounded-2xl p-6 w-[460px] max-h-[80vh] overflow-y-auto text-white text-sm" onClick=${(e) => e.stopPropagation()}>
            <div className="text-lg font-bold mb-3">Credits & attributions</div>
            <div className="text-white/60 text-xs mb-3">3D animal models via Poly Pizza, used under CC-BY 3.0:</div>
            <ul className="space-y-2">
              ${CREDITS.map((c, i) => html`<li key=${i} className="text-white/80 leading-snug">${c}</li>`)}
            </ul>
            <button onClick=${() => setShowCredits(false)} className="mt-4 rounded-xl px-4 py-2 bg-emerald-500 hover:bg-emerald-400 font-bold">Close</button>
          </div>
        </div>`}
    </div>
  `;
}

function LoadingScreen() {
  const [progress, setProgress] = useState(0);
  useEffect(() => bus.on('loading:progress', setProgress), []);
  return html`
    <div className="fixed inset-0 flex items-center justify-center bg-[#0a0f14] pointer-events-auto">
      <div className="glass-dark rounded-2xl p-8 w-80 text-center text-white">
        <div className="text-lg font-semibold mb-4">Preparing your zoo…</div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-emerald-400 rounded-full transition-all" style=${{ width: `${Math.max(progress * 100, 8)}%` }}></div>
        </div>
      </div>
    </div>
  `;
}

function HUD() {
  const s = useGameState();
  const panel = s.openPanel || null; // 'finance'|'animals'|'book'|'tv'|'overlays'|'terrain'|'infra'|'shop-animals'|'shop-buildings' — max one open
  const setPanel = (p) => store.set({ openPanel: p });
  const [showHelp, setShowHelp] = useState(!s.kidsMode);
  const toggle = (p) => setPanel(panel === p ? null : p);
  useEffect(() => bus.on('ui:panel', (p) => { const cur = store.get().openPanel || null; store.set({ openPanel: cur === p ? null : p }); }), []);
  useEffect(() => bus.on('enclosure:selected', (e) => { if (e && e.id != null && !store.get().activeTool) store.set({ openPanel: 'habitat', selectedEnc: e.id }); }), []);
  useEffect(() => bus.on('amenity:selected', (e) => { if (e && !store.get().activeTool) store.set({ openPanel: 'building', selBuilding: e }); }), []);
  return html`
    <div className="fixed inset-0 pointer-events-none">
      ${s.mandateOffer ? html`<${MandateDialog} offer=${s.mandateOffer} />` : ''}
      ${(s.cullOffers && s.cullOffers.length) ? html`<${CullDialog} offer=${s.cullOffers[0]} />` : ''}
      ${s.contractOffer ? html`<${ContractDialog} offer=${s.contractOffer} />` : ''}
      ${panel === 'finance' && html`<${FinancePanel} s=${s} onClose=${() => setPanel(null)} />`}
      ${panel === 'save' && html`<${SavePanel} onClose=${() => setPanel(null)} />`}
      ${panel === 'visitors' && html`<${VisitorsPanel} onClose=${() => setPanel(null)} />`}
      ${panel === 'animals' && html`<${AnimalManager} kidsMode=${s.kidsMode} onClose=${() => setPanel(null)} />`}
      ${panel === 'book' && html`<${StickerBook} collected=${s.collected || []} trophies=${s.trophies || []} onClose=${() => setPanel(null)} />`}
      ${panel === 'tv' && html`<${ZooTV} feed=${s.newsFeed} onClose=${() => setPanel(null)} />`}
      ${panel === 'overlays' && html`<${OverlaysPanel} s=${s} onClose=${() => setPanel(null)} />`}
      ${panel === 'terrain' && html`<${TerrainPanel} onClose=${() => setPanel(null)} />`}
      ${panel === 'infra' && html`<${InfraPanel} onClose=${() => setPanel(null)} />`}
      ${panel === 'habitat' && html`<${HabitatPanel} id=${s.selectedEnc} onClose=${() => setPanel(null)} />`}
      ${panel === 'building' && html`<${BuildingInfoPanel} sel=${s.selBuilding} onClose=${() => setPanel(null)} />`}
      ${panel === 'settings' && html`<${SettingsPanel} onClose=${() => setPanel(null)} />`}
      ${panel === 'shop-animals' && html`<${AnimalShopPanel} onClose=${() => setPanel(null)} />`}
      ${panel === 'shop-buildings' && html`<${BuildingShopPanel} onClose=${() => setPanel(null)} />`}
      <${AnimalPanel} />
      <${Toolbar} activeTool=${s.activeTool} kidsMode=${s.kidsMode} openPanel=${panel} />
      <${Confetti} />
      ${s.mayor && html`
        <div className="absolute left-4 glass-dark rounded-2xl px-5 py-3 text-white pointer-events-auto shadow-xl max-w-[17rem] border-l-4 border-amber-400" style=${{ top: '11rem' }}>
          <div className="text-[10px] uppercase tracking-widest text-amber-300/80 mb-1">Mayor's Request · ${s.mayor.daysLeft}d left</div>
          <div className="text-sm font-semibold leading-snug">🏛️ ${s.mayor.text}</div>
          <div className="text-xs text-amber-300 mt-1">Reward: $${s.mayor.reward.toLocaleString('en-US')}</div>
        </div>
      `}
      ${s.dilemma && html`
        <div className="absolute left-1/2 -translate-x-1/2 glass-dark rounded-2xl p-5 text-white pointer-events-auto shadow-2xl w-80" style=${{ top: '150px', zIndex: 55 }}>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 mb-2">Decision time!</div>
          <div className="text-sm font-semibold leading-snug mb-4">${s.dilemma.text}</div>
          <div className="flex gap-2">
            <button onClick=${() => bus.emit('dilemma:choose', s.dilemma.a)} className="flex-1 rounded-xl py-2 bg-emerald-500 hover:bg-emerald-400 font-bold text-sm transition-colors">${s.dilemma.a.label}</button>
            <button onClick=${() => bus.emit('dilemma:choose', s.dilemma.b)} className="flex-1 rounded-xl py-2 glass hover:bg-white/15 font-bold text-sm">${s.dilemma.b.label}</button>
          </div>
        </div>
      `}
      ${s.report && html`
        <div className="absolute left-1/2 -translate-x-1/2 glass-dark rounded-2xl p-5 text-white pointer-events-auto shadow-2xl w-80" style=${{ top: '150px', zIndex: 55 }}>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 mb-2">Year ${s.report.year} Report Card</div>
          <div className="text-4xl font-bold text-center mb-2">${s.report.grade}</div>
          <div className="text-xs text-white/60 text-center mb-4">${s.report.visitors.toLocaleString('en-US')} visitors total · ${'⭐'.repeat(s.report.stars)}</div>
          <div className="text-xs text-white/60 mb-2">Choose a permanent perk:</div>
          <div className="flex flex-col gap-2">
            ${PERKS.filter((p) => !(s.perks || []).includes(p.id)).map((p) => html`
              <button key=${p.id} onClick=${() => bus.emit('perk:choose', p.id)} className="rounded-xl py-2 px-3 glass hover:bg-white/15 text-left text-sm">
                ${p.icon} <span className="font-semibold">${p.label}</span> <span className="text-white/50 text-xs">— ${p.desc}</span>
              </button>
            `)}
          </div>
        </div>
      `}
      <${EnclosurePanel} />

      <!-- Top stack: bar, then mission, then notifications — never overlapping -->
      <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-none" style=${{ top: '12px', maxWidth: '96vw' }}>
      <div className="zptop pointer-events-auto">
        <div className="zptop-zoo"><span className="zptop-live"></span>${s.zooName}</div>
        <div className="zptop-div"></div>
        <div className="zptop-seg"><span className="i">🪙</span><span className="v" style=${{ color: '#fcd34d' }}>${s.kidsMode ? Math.max(0, Math.round(s.money / 100)).toLocaleString('en-US') : '$' + s.money.toLocaleString('en-US')}</span><${SaleFlash} kidsMode=${s.kidsMode} /></div>
        <div className="zptop-seg" title="Visitors"><span className="i">🧑‍🤝‍🧑</span><span className="v">${(s.visitorCount || 0).toLocaleString('en-US')}</span></div>
        <div className="zptop-seg" title="Adult animals vs your government permit"><span className="i">🦁</span><span className="v" style=${{ color: (s.adultCount || 0) >= (s.animalCap || 0) ? '#fca5a5' : (s.adultCount || 0) >= (s.animalCap || 1) * 0.85 ? '#fcd34d' : '#a7f3d0' }}>${s.adultCount || 0}/${s.animalCap || 0}</span></div>
        <div className="zptop-seg" title=${s.rivalStars ? 'Your stars vs Safari World' : 'Zoo stars'}><span className="i">⭐</span><span className="v">${(s.stars || 1) + (s.rivalStars ? ' vs ' + s.rivalStars : '')}</span></div>
        <div className="zptop-seg" title="Reputation"><span className="i">❤️</span><span className="v" style=${{ color: '#f9a8d4' }}>${s.reputation}%</span></div>
        <div className="zptop-seg" title="Prestige — unlocks new animals"><span className="i">🏅</span><span className="v">${s.prestige}</span></div>
        <div className="zptop-div"></div>
        <div className="zptop-season">${SEASON_ICONS[s.season]} ${MONTHS[s.month - 1]} ${s.day}${s.weather ? (s.weather === 'storm' ? ' ⛈️' : ' 🔥') : ''}</div>
        ${s.inspection ? html`<div className="zptop-div"></div><div className="zptop-seg" title="Last welfare inspection grade"><span className="i">🏛️</span><span className="v">${s.inspection.grade}</span></div>` : ''}
        <div className="zptop-div"></div>
        ${s.sickTotal ? html`<div className="zptop-seg" title="Sick animals — open Health lens" style=${{ cursor: 'pointer' }} onClick=${() => store.set({ openPanel: 'overlays', overlayLens: 'health' })}><span className="i">🤒</span><span className="v" style=${{ color: '#fca5a5' }}>${s.sickTotal}</span></div><div className="zptop-div"></div>` : ''}
        <button className="zptop-ico" title="Fly home" onClick=${() => bus.emit('camera:home')}>🏠</button>
        <button className=${'zptop-ico' + (showHelp ? ' on' : '')} title="Help" onClick=${() => setShowHelp(!showHelp)}>❓</button>
        <button className=${'zptop-ico' + (panel === 'settings' ? ' on' : '')} title="Graphics settings" onClick=${() => bus.emit('ui:panel', 'settings')}>⚙️</button>
        <div className="zptop-speed">
          ${[0, 1, 2, 3].map((sp) => html`<button key=${sp} onClick=${() => store.set({ speed: sp })} className=${'zptop-sp' + (s.speed === sp ? ' on' : '')}>${sp === 0 ? '⏸' : '×' + sp}</button>`)}
        </div>
        ${s.zooClosed ? html`<div className="zptop-closed">CLOSED</div>` : ''}
      </div>
      <${MissionBanner} mission=${s.mission} />
      <${NotificationCenter} />
      </div>

      <${NewsTickerBar} feed=${s.newsFeed} onOpenTV=${() => toggle('tv')} />
      <${GettingStarted} />
      <${PerfStats} />

      ${showHelp && html`
        <div className="absolute bottom-16 left-4 glass-dark rounded-xl px-4 py-2 text-xs text-white/50 pointer-events-auto whitespace-nowrap">
          WASD · R-drag rotate · M-drag pan · Scroll zoom${s.fps ? html` · <span className=${s.fps >= 50 ? 'text-emerald-400' : s.fps >= 30 ? 'text-amber-300' : 'text-red-400'}>${s.fps} fps</span>` : ''}
        </div>
      `}
    </div>
  `;
}

function Pill({ bg, border, icon, value, title }) {
  return html`
    <div title=${title || ''} className=${'shrink-0 rounded-full border px-3 h-9 flex items-center gap-1.5 text-sm font-bold ' + bg + ' ' + border}>
      <span>${icon}</span><span>${value}</span>
    </div>
  `;
}

function IconBtn({ icon, title, active, onClick }) {
  return html`
    <button onClick=${onClick} title=${title}
      className=${'w-10 h-10 rounded-xl text-base transition-all hover:text-white hover:bg-white/15 ' + (active ? 'bg-white/25 text-white shadow-inner' : 'glass text-white/70')}>${icon}</button>
  `;
}

const TOOLS = [
  { id: 'bulldoze', icon: '💥', label: 'Bulldoze', cost: '40% back', color: 'bg-red-500' },
  { id: '__undo', icon: '↩️', label: 'Undo', cost: 'Ctrl+Z', color: 'bg-slate-500' },
];
const INFRA_TOOLS = [
  { id: 'path', icon: '🛤️', label: 'Path' },
  { id: 'enclosure', icon: '🏕️', label: 'Habitat' },
  { id: 'fence', icon: '🚧', label: 'Fence' },
];

// Confetti + jingle on celebrations (missions, babies). DOM canvas overlay, no assets needed.
// One shared AudioContext + one shared rAF loop — creating these per event tanked the framerate.
let _audioCtx = null;
function playJingle(kind) {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const notes = kind === 'baby' ? [523, 659, 784, 1047] : [523, 659, 784];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = f; o.type = 'triangle';
      g.gain.setValueAtTime(0.12, ctx.currentTime + i * 0.12);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.35);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.12); o.stop(ctx.currentTime + i * 0.12 + 0.4);
    });
  } catch { /* audio blocked */ }
}

function Confetti() {
  // Celebration = jingle only. The old full-screen confetti canvas composited
  // over the WebGL view every frame and tanked the framerate on every event.
  useEffect(() => bus.on('ui:celebrate', ({ kind }) => playJingle(kind)), []);
  return null;
}

// A single bottom-bar menu button. Highlights while its side panel is open
// (or, for tool groups, while a matching tool is active).
function MenuBtn({ icon, label, active, activeClass, onClick }) {
  const on = active ? ((activeClass || 'bg-white/20 text-white') + ' shadow-lg') : 'text-white/70 hover:text-white hover:bg-white/10';
  return html`
    <button onClick=${onClick} className=${'flex flex-col items-center justify-center rounded-2xl w-16 h-16 transition-all ' + on}>
      <span className="text-2xl leading-none">${icon}</span>
      <span className="text-[11px] mt-1 font-semibold">${label}</span>
    </button>
  `;
}

// Shared right-side sliding-panel shell — same frame as Finance / Overlays / Zoo TV.
// Header and the optional pinned tab row stay fixed; only the content area scrolls,
// so tabs are always reachable (no scrolling them off screen).
function SidePanel({ icon, title, subtitle, accent, onClose, tabs, noPad, children }) {
  const a = accent || ['#2f6fed', '#5fa2ff'];
  return html`
    <div className="zp" style=${{ '--zp-accent': a[0], '--zp-accent2': a[1] }}>
      <div className="zp-head">
        <div className="zp-chip">${icon}</div>
        <div style=${{ minWidth: 0 }}>
          <div className="zp-title">${title}</div>
          <div className="zp-sub">${subtitle}</div>
        </div>
        <button onClick=${onClose} className="zp-close">✕</button>
      </div>
      ${tabs ? html`<div className="zp-tabwrap">${tabs}</div>` : ''}
      <div className=${'zp-body' + (noPad ? ' nopad' : '')}>${children}</div>
    </div>
  `;
}

// Pinned segmented tabs. Full-width targets so a tap always lands — this is what
// makes the Animals / Buildings tabs reliable.
function TabRow({ tabs, active, onSelect }) {
  return html`
    <div className="zp-tabs">
      ${tabs.map(([id, label]) => html`
        <button key=${id} type="button" onClick=${() => onSelect(id)}
          className=${'zp-tab' + (active === id ? ' active' : '')}>${label}</button>`)}
    </div>
  `;
}

// A tool row like the Overlays panel entries: icon · label + description · ON badge.
function ToolRow({ id, icon, label, desc, activeTool }) {
  const sel = activeTool === id;
  return html`
    <button type="button" onClick=${() => store.set({ activeTool: sel ? null : id })}
      className=${'zp-trow' + (sel ? ' sel' : '')}>
      <span className="zp-ti">${icon}</span>
      <span style=${{ flex: 1, textAlign: 'left', minWidth: 0 }}>
        <span className="zp-tt" style=${{ display: 'block' }}>${label}</span>
        <span className="zp-td" style=${{ display: 'block' }}>${desc}</span>
      </span>
      ${sel ? html`<span style=${{ fontSize: '10px', fontWeight: 800, letterSpacing: '.08em', color: 'var(--zp-accent2)' }}>ON</span>` : ''}
    </button>
  `;
}

function TerrainPanel({ onClose }) {
  const activeTool = useGameState().activeTool;
  const tools = [
    ['terraform-raise', '⛰️', 'Raise', 'Push the ground up into hills'],
    ['terraform-lower', '🕳️', 'Lower', 'Dig down for valleys & lakes'],
    ['terraform-flatten', '🏗️', 'Flatten', 'Level the ground ready to build'],
  ];
  return html`
    <${SidePanel} icon="⛰️" title="Terrain" subtitle="Sculpt the landscape — drag on the map"
      accent=${['#78716c', '#a8a29e']} onClose=${onClose}>
      ${tools.map(([id, icon, label, desc]) => html`<${ToolRow} key=${id} id=${id} icon=${icon} label=${label} desc=${desc} activeTool=${activeTool} />`)}
      <div className="zp-muted2" style=${{ fontSize: '11.5px', marginTop: '2px' }}>Pick a tool, then click-drag across the map to shape the land.</div>
    <//>
  `;
}

function InfraPanel({ onClose }) {
  const activeTool = useGameState().activeTool;
  const tools = [
    ['path', '🛤️', 'Path', 'Walkways your guests follow'],
    ['enclosure', '🏕️', 'Habitat', 'Drag a rectangle to fence a habitat'],
    ['fence', '🚧', 'Fence', 'Add or remove single fence segments'],
  ];
  return html`
    <${SidePanel} icon="🧱" title="Infrastructure" subtitle="Paths, habitats and fences"
      accent=${['#0284c7', '#38bdf8']} onClose=${onClose}>
      ${tools.map(([id, icon, label, desc]) => html`<${ToolRow} key=${id} id=${id} icon=${icon} label=${label} desc=${desc} activeTool=${activeTool} />`)}
      <div className="zp-muted2" style=${{ fontSize: '11.5px', marginTop: '2px' }}>Habitats need to be fully fenced before animals can live in them.</div>
    <//>
  `;
}

function AnimalShopPanel({ onClose }) {
  const s = useGameState();
  const [tab, setTab] = useState('all');
  const [, force] = useState(0);
  useEffect(() => bus.on('animals:thumbs', () => force((t) => t + 1)), []);
  const { activeTool, prestige } = s;
  const full = (s.adultCount || 0) >= (s.animalCap || Infinity);
  const list = Object.entries(SPECIES)
    .filter(([key]) => tab === 'all' || animalCat(key) === tab)
    .sort((a, b) => a[1].minPrestige - b[1].minPrestige);
  const tabs = html`<${TabRow} active=${tab} onSelect=${setTab} tabs=${[['all', 'All'], ['predators', 'Predators'], ['prey', 'Prey'], ['other', 'Birds & Pets']]} />`;
  return html`
    <${SidePanel} icon="🦁" title="Animal Shop" subtitle="Pick an animal, then click a habitat to place it"
      accent=${['#d97706', '#f59e0b']} onClose=${onClose} tabs=${tabs}>
      ${full ? html`<div className="zp-card" style=${{ borderColor: 'rgba(251,113,133,.4)', background: 'rgba(251,113,133,.10)', marginBottom: '2px' }}>
        <div style=${{ fontSize: '13px', fontWeight: 700, color: '#fda4af' }}>🏛️ Permit full — ${s.adultCount}/${s.animalCap} adults</div>
        <div className="zp-muted2" style=${{ fontSize: '11.5px', marginTop: '2px' }}>Raise your government permit in the Genetics Lab to keep more animals.</div>
      </div>` : ''}
      <div className="zp-grid" style=${{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
        ${list.map(([key, sp]) => {
          const locked = prestige < sp.minPrestige;
          const sel = activeTool === 'animal-' + key;
          return html`
            <button key=${key} type="button" disabled=${locked}
              onClick=${() => store.set({ activeTool: sel ? null : 'animal-' + key })}
              title=${locked ? 'Unlocks at ' + sp.minPrestige + ' prestige' : sp.label}
              className=${'zp-tile' + (locked ? ' lock' : '') + (sel ? ' sel' : '')}>
              <span className="zp-thumb">${ANIMAL_THUMBS[key] ? html`<img src=${ANIMAL_THUMBS[key]} alt=${sp.label} className=${locked ? 'grayscale' : ''} />` : (locked ? '🔒' : '🐾')}</span>
              <span className="zp-tname">${sp.label}</span>
              <span className="zp-price">${locked ? sp.minPrestige + ' 🏅' : '$' + sp.price.toLocaleString('en-US')}</span>
            </button>`;
        })}
      </div>
    <//>
  `;
}

function BuildingShopPanel({ onClose }) {
  const s = useGameState();
  const [tab, setTab] = useState('main');
  const [, force] = useState(0);
  useEffect(() => bus.on('amenities:thumbs', () => force((t) => t + 1)), []);
  const activeTool = s.activeTool;
  const tabs = html`<${TabRow} active=${tab} onSelect=${setTab} tabs=${[['main', 'Main Buildings'], ['props', 'Props'], ['fillers', 'Enclosure']]} />`;
  const mainItems = Object.entries(AMENITIES).filter(([key]) => key !== 'entry');
  const propItems = Object.entries(PROPS).filter(([, def]) => (tab === 'fillers' ? def.enclosure : !def.enclosure));
  return html`
    <${SidePanel} icon="🏪" title="Buildings & Props" subtitle="Shops, facilities and decorations"
      accent=${['#7c3aed', '#a855f7']} onClose=${onClose} tabs=${tabs}>
      ${tab === 'main' ? html`
        <div className="zp-grid" style=${{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          ${mainItems.map(([key, def]) => {
            const sel = activeTool === 'amenity-' + key;
            return html`
              <button key=${key} type="button" onClick=${() => store.set({ activeTool: sel ? null : 'amenity-' + key })}
                className=${'zp-tile' + (sel ? ' sel' : '')}>
                <span className="zp-thumb">${AMENITY_THUMBS[key] ? html`<img src=${AMENITY_THUMBS[key]} alt=${def.label} />` : '🏛️'}</span>
                <span className="zp-tname">${def.label}</span>
                <span className="zp-price">$${def.price.toLocaleString('en-US')}</span>
              </button>`;
          })}
        </div>
      ` : html`
        <div className="zp-grid" style=${{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          ${propItems.map(([key, def]) => {
            const sel = activeTool === 'prop-' + key;
            return html`
              <button key=${'prop-' + key} type="button" onClick=${() => store.set({ activeTool: sel ? null : 'prop-' + key })}
                className=${'zp-tile' + (sel ? ' sel' : '')}>
                <span className="zp-thumb" style=${{ fontSize: '30px' }}>${def.icon}</span>
                <span className="zp-tname">${def.label}</span>
                <span className="zp-price">$${def.price.toLocaleString('en-US')}</span>
              </button>`;
          })}
          ${propItems.length === 0 ? html`<div className="zp-muted" style=${{ gridColumn: '1 / -1', textAlign: 'center', padding: '24px 0', fontSize: '13px' }}>Nothing here yet.</div>` : ''}
        </div>
      `}
    <//>
  `;
}

function Toolbar({ activeTool, kidsMode, openPanel }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [devFront, setDevFront] = useState(!!window.__devFront);
  useEffect(() => {
    const off = bus.on('dev:front', (on) => setDevFront(on));
    return off;
  }, []);
  const isAnimalTool = activeTool && activeTool.startsWith('animal-');
  const isAmenityTool = activeTool && activeTool.startsWith('amenity-');
  const isPropTool = activeTool && activeTool.startsWith('prop-');
  const isTerrainTool = activeTool && activeTool.startsWith('terraform-');
  const isInfraTool = ['path', 'enclosure', 'fence'].includes(activeTool);
  const openP = (p) => bus.emit('ui:panel', p);
  return html`
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-auto">
      <div className="glass-dark rounded-2xl px-3 py-2 flex gap-1 shadow-xl">
        <${MenuBtn} icon="🧑‍🤝‍🧑" label="Visitors" active=${openPanel === 'visitors'} onClick=${() => openP('visitors')} />
        <${MenuBtn} icon="🧬" label="DNA" active=${openPanel === 'animals'} onClick=${() => openP('animals')} />
        <${MenuBtn} icon="💰" label="Finance" active=${openPanel === 'finance'} onClick=${() => openP('finance')} />
        <${MenuBtn} icon="👁️" label="Overlays" active=${openPanel === 'overlays'} onClick=${() => openP('overlays')} />
        <${MenuBtn} icon="📺" label="Zoo TV" active=${openPanel === 'tv'} onClick=${() => openP('tv')} />
        <${MenuBtn} icon="📖" label="Stickers" active=${openPanel === 'book'} onClick=${() => openP('book')} />
        <div className="w-px self-stretch my-1 bg-white/20 mx-1"></div>
        <${MenuBtn} icon="⛰️" label="Terrain" active=${openPanel === 'terrain' || isTerrainTool} activeClass="bg-stone-500 text-white scale-110" onClick=${() => openP('terrain')} />
        <${MenuBtn} icon="🧱" label="Infra" active=${openPanel === 'infra' || isInfraTool} activeClass="bg-sky-600 text-white scale-110" onClick=${() => openP('infra')} />
        ${kidsMode && html`<${MenuBtn} icon="⚙️" label="More" active=${moreOpen} onClick=${() => setMoreOpen(!moreOpen)} />`}
        <${MenuBtn} icon="🦁" label="Animals" active=${openPanel === 'shop-animals' || isAnimalTool} activeClass="bg-amber-500 text-white scale-110" onClick=${() => openP('shop-animals')} />
        <${MenuBtn} icon="🏪" label="Buildings" active=${openPanel === 'shop-buildings' || isAmenityTool || isPropTool} activeClass="bg-purple-500 text-white scale-110" onClick=${() => openP('shop-buildings')} />
        <div className="w-px self-stretch my-1 bg-white/20 mx-1"></div>
        ${TOOLS.map((t) => html`
          <button
            key=${t.id}
            onClick=${() => { if (t.id === '__undo') { bus.emit('build:undo'); return; } store.set({ activeTool: activeTool === t.id ? null : t.id }); }}
            title=${t.label + ' (' + t.cost + ')'}
            className=${'flex flex-col items-center justify-center rounded-2xl w-16 h-16 transition-all ' + (activeTool === t.id ? t.color + ' text-white shadow-lg scale-110' : 'text-white/70 hover:text-white hover:bg-white/10')}
          >
            <span className="text-2xl leading-none">${t.icon}</span>
            <span className="text-[11px] mt-1 font-semibold">${t.label}</span>
          </button>
        `)}
        <div className="w-px self-stretch my-1 bg-white/20 mx-1"></div>
        <${MenuBtn} icon="💾" label="Save" active=${openPanel === 'save'} onClick=${() => openP('save')} />
      </div>
    </div>
  `;
}

function EnclosurePanel() {
  const [sel, setSel] = useState(null);
  useEffect(() => bus.on('enclosure:selected', (enc) => {
    if (!enc) { setSel(null); return; }
    const w = window.__world;
    const animals = w ? w.animals.animals.filter((a) => a.enclosureId === enc.id && !a.escaped) : [];
    const avg = animals.length ? animals.reduce((s, a) => s + a.wellbeing, 0) / animals.length : null;
    let face = '😀', advice = 'Everyone is happy here!';
    if (!animals.length) { face = '🦁'; advice = 'This habitat is empty — put some animals in!'; }
    else if (avg < 40) { face = '😢'; advice = 'Sad animals! Make the habitat bigger or add a friend of the same kind.'; }
    else if (avg < 75) { face = '😐'; advice = 'Doing okay — more space or more friends would make them happier.'; }
    setSel({ size: enc.size, count: animals.length, face, advice });
  }), []);
  if (!sel) return null;
  return html`
    <div className="absolute top-24 right-4 glass-dark rounded-2xl px-5 py-4 text-white pointer-events-auto shadow-xl max-w-[16rem]">
      <div className="flex items-start justify-between gap-2">
        <span className="text-4xl leading-none">${sel.face}</span>
        <button onClick=${() => setSel(null)} className="text-white/40 hover:text-white text-lg leading-none">✕</button>
      </div>
      <div className="text-sm mt-2 leading-snug">${sel.advice}</div>
      <div className="text-[11px] text-white/40 mt-2">${sel.count} animals · ${sel.size} cells</div>
    </div>
  `;
}

function MissionBanner({ mission }) {
  if (!mission) return null;
  return html`
    <div className="glass-dark rounded-2xl pl-4 pr-6 py-2.5 text-white pointer-events-auto shadow-xl flex items-center gap-3 border-b-4 border-emerald-400/70">
      <span className="text-3xl">${mission.icon}</span>
      <div>
        <div className="text-[9px] uppercase tracking-widest text-emerald-300/80">Mission</div>
        <div className="text-sm font-bold leading-snug">${mission.text}</div>
      </div>
      <div className="text-xs font-bold text-amber-300 bg-amber-400/15 border border-amber-300/30 rounded-full px-2.5 py-1">+$${mission.reward.toLocaleString('en-US')}</div>
    </div>
  `;
}

// ONE notification at a time, queued; "unlocked" bursts merge into a single card.
function NotificationCenter() {
  const [current, setCurrent] = useState(null);
  useEffect(() => {
    let queue = [];
    let showing = false;
    let timer = null;
    const showNext = () => {
      if (!queue.length) { showing = false; setCurrent(null); return; }
      showing = true;
      // merge all queued unlocks into one
      const unlocks = queue.filter((t) => t.text.includes('unlocked'));
      let item;
      if (unlocks.length > 1) {
        item = { text: `🔓 ${unlocks.length} new animals unlocked!`, kind: 'success' };
        queue = queue.filter((t) => !t.text.includes('unlocked'));
      } else {
        item = queue.shift();
      }
      setCurrent(item);
      timer = setTimeout(showNext, 2600);
    };
    const un = bus.on('ui:toast', (t) => {
      queue.push(t);
      if (queue.length > 8) queue = queue.slice(-8);
      if (!showing) showNext();
    });
    return () => { un(); clearTimeout(timer); };
  }, []);
  if (!current) return null;
  return html`
    <div className="pointer-events-none" style=${{ zIndex: 45 }}>
      <div className=${'glass-dark rounded-2xl px-5 py-3 text-base font-bold shadow-2xl border-b-4 ' + (current.kind === 'error' ? 'text-red-300 border-red-400/70' : current.kind === 'success' ? 'text-emerald-200 border-emerald-400/70' : 'text-white border-white/30')}>${current.text}</div>
    </div>
  `;
}

// First-session onboarding: a live checklist of starter tasks in the panel glass style. Auto-ticks
// from world state, dismissible, and the dismissal persists (localStorage) so it never nags on reload.
function GettingStarted() {
  const [, force] = useState(0);
  const [seenFinance, setSeenFinance] = useState(false);
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem('zoo_guide_done') === '1'; } catch (e) { return false; } });
  useEffect(() => { const t = setInterval(() => force((x) => x + 1), 900); return () => clearInterval(t); }, []);
  useEffect(() => { if (store.get().openPanel === 'finance') setSeenFinance(true); });
  if (hidden) return null;
  const w = window.__world; const st = store.get();
  if (!w || !w.build || st.screen !== 'game') return null;
  const serves = (need) => !!(w.amenities && w.amenities.items.some((i) => (AMENITIES[i.type] || {}).serves === need));
  const steps = [
    ['\uD83D\uDEE0\uFE0F', 'Lay a path', w.build.paths.size > 0],
    ['\uD83E\uDDF1', 'Fence an enclosure', (w.build.enclosures || []).length > 0],
    ['\uD83E\uDD81', 'Add an animal', !!(w.animals && w.animals.animals.length)],
    ['\uD83C\uDF54', 'Build a food stand', serves('hunger')],
    ['\uD83E\uDD64', 'Build a drink stand', serves('thirst')],
    ['\uD83D\uDCB0', 'Open Finance', seenFinance],
    ['\uD83E\uDDD1\u200D\uD83E\uDD1D\u200D\uD83E\uDDD1', 'Reach 10 visitors', (st.visitorCount || 0) >= 10],
  ];
  const done = steps.filter((x) => x[2]).length;
  const all = done === steps.length;
  if (all) { try { localStorage.setItem('zoo_guide_done', '1'); } catch (e) {} }
  const dismiss = () => { try { localStorage.setItem('zoo_guide_done', '1'); } catch (e) {} setHidden(true); };
  return html`
    <div className="glass-dark pointer-events-auto" style=${{ position: 'absolute', left: '14px', bottom: '92px', width: '244px', borderRadius: '16px', padding: '13px 14px', color: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,.4)' }}>
      <div style=${{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <span style=${{ fontSize: '14px', fontWeight: 800, flex: 1 }}>Getting started</span>
        <span className="zp-muted" style=${{ fontSize: '11px', fontWeight: 700 }}>${done}/${steps.length}</span>
        <button type="button" onClick=${dismiss} title="Dismiss" style=${{ background: 'none', border: 'none', color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0 }}>\u00d7</button>
      </div>
      <div style=${{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        ${steps.map((stp, i) => html`<div key=${i} style=${{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12.5px', opacity: stp[2] ? 0.5 : 1 }}>
          <span style=${{ width: '18px', height: '18px', flex: '0 0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#062', background: stp[2] ? 'rgba(52,211,153,.95)' : 'rgba(255,255,255,.09)', border: stp[2] ? 'none' : '1px solid rgba(255,255,255,.22)' }}>${stp[2] ? '\u2713' : ''}</span>
          <span style=${{ textDecoration: stp[2] ? 'line-through' : 'none' }}>${stp[0]} ${stp[1]}</span>
        </div>`)}
      </div>
      ${all ? html`<div style=${{ marginTop: '11px', fontSize: '11.5px', fontWeight: 700, color: '#6ee7b7' }}>All set \u2014 enjoy building Arkadia! \uD83C\uDF89</div>` : ''}
    </div>`;
}

// Live performance stats overlay (additive, read-only) — self-samples frame-time; reads renderer.info
// for draw calls & triangles. Toggled from Settings. Never touches the render pipeline.
function PerfStats() {
  const s = useGameState();
  const on = !!(s.graphics || {}).showStats;
  const [, force] = useState(0);
  const ref = React.useRef({ samples: [], last: 0, raf: 0 });
  useEffect(() => {
    if (!on) return;
    let alive = true;
    ref.current.last = performance.now();
    const tick = () => {
      if (!alive) return;
      const now = performance.now(), dt = now - ref.current.last; ref.current.last = now;
      const a = ref.current.samples; a.push(dt); if (a.length > 150) a.shift();
      ref.current.raf = requestAnimationFrame(tick);
    };
    ref.current.raf = requestAnimationFrame(tick);
    const iv = setInterval(() => force((x) => x + 1), 500);
    return () => { alive = false; cancelAnimationFrame(ref.current.raf); clearInterval(iv); };
  }, [on]);
  if (!on) return null;
  const a = ref.current.samples;
  const avg = a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0;
  const sorted = [...a].sort((x, y) => x - y);
  const low = sorted.length ? (sorted[Math.floor(sorted.length * 0.99)] || sorted[sorted.length - 1]) : 0;
  const fps = avg ? Math.round(1000 / avg) : 0;
  const w = window.__world;
  const info = (w && w.renderer && w.renderer.renderer) ? w.renderer.renderer.info.render : null;
  const row = (label, val, col) => html`<div style=${{ display: 'flex', justifyContent: 'space-between', gap: '14px' }}><span className="zp-muted">${label}</span><span style=${col ? { color: col } : {}}>${val}</span></div>`;
  return html`<div className="glass-dark pointer-events-none" style=${{ position: 'absolute', top: '58px', left: '14px', padding: '9px 12px', borderRadius: '12px', font: '600 11px ui-monospace,SFMono-Regular,monospace', color: '#fff', lineHeight: 1.55, minWidth: '132px', boxShadow: '0 8px 22px rgba(0,0,0,.4)' }}>
    ${row('FPS', fps, fps >= 50 ? '#34d399' : fps >= 30 ? '#fbbf24' : '#fb7185')}
    ${row('frame', avg.toFixed(1) + 'ms')}
    ${row('1% low', low.toFixed(0) + 'ms', low <= 20 ? '#34d399' : low <= 40 ? '#fbbf24' : '#fb7185')}
    ${info ? html`${row('draws', info.calls)}${row('tris', (info.triangles / 1000).toFixed(0) + 'k')}` : ''}
  </div>`;
}

// Bottom news ticker: one-line marquee across the screen; click opens Zoo TV.
function NewsTickerBar({ feed, onOpenTV }) {
  if (!feed.length) return null;
  const items = feed.slice(0, 6);
  return html`
    <div onClick=${onOpenTV} className="absolute bottom-0 inset-x-0 h-9 glass-dark border-t border-white/10 flex items-center pointer-events-auto cursor-pointer overflow-hidden" title="Open Zoo TV">
      <div className="shrink-0 h-full flex items-center gap-1.5 bg-red-500/80 text-white text-xs font-bold z-10" style=${{ paddingLeft: '12px', paddingRight: '40px', clipPath: 'polygon(0 0, 100% 0, calc(100% - 28px) 100%, 0 100%)' }}>📺 ZOO NEWS</div>
      <div className="ticker-track flex items-center gap-12 pl-4 text-xs whitespace-nowrap">
        ${items.map((n) => html`
          <span key=${n.id} className=${n.kind === 'success' ? 'text-emerald-300' : n.kind === 'error' ? 'text-red-300' : 'text-white/80'}>${n.headline}</span>
        `)}
      </div>
    </div>
  `;
}

// Building panel: click a shop/facility to override its service level (Budget/Standard/Premium),
// open/close it, or rename it — deviating from the category default. Reads the live item.
function BuildingInfoPanel({ sel, onClose }) {
  const [, force] = useState(0);
  useEffect(() => { const t = setInterval(() => force((n) => n + 1), 700); return () => clearInterval(t); }, []);
  const w = window.__world;
  const item = (w && w.amenities && sel) ? w.amenities.byCell.get(sel.cx + ',' + sel.cz) : null;
  const def = item ? AMENITIES[item.type] : null;
  const eco = w && w.economy;
  const curTier = (item && eco) ? eco.buildingTier(item) : 1;
  const income = (item && w.overlays) ? Math.round(w.overlays.income.get(item.cx + ',' + item.cz) || 0) : 0;
  const TIERS = [['Budget', 0], ['Standard', 1], ['Premium', 2]];
  const stepBtn = { width: '34px', height: '34px', borderRadius: '9px', border: '1px solid rgba(255,255,255,.14)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: '18px', fontWeight: 700, cursor: 'pointer' };
  return html`
    <${SidePanel} icon="🏪" title=${item ? (item.name || def.label) : 'Building'} subtitle="Per-building service · staffing · status"
      accent=${['#7c3aed', '#a855f7']} onClose=${onClose}>
      ${!item ? html`<div className="zp-muted" style=${{ padding: '20px 4px', fontSize: '13px' }}>Click a shop or facility on the map to manage it.</div>` : html`
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Service level</span></div>
          <div className="zp-muted2" style=${{ fontSize: '12px', marginBottom: '10px' }}>Overrides the category default for this building. Higher tier = better staffing & products → more sales and happiness, but higher daily upkeep.</div>
          <div style=${{ display: 'flex', gap: '6px' }}>
            ${TIERS.map(([lab, t]) => html`<button key=${lab} type="button" onClick=${() => { item.tier = t; force((n) => n + 1); }}
              style=${{ flex: 1, padding: '9px 0', borderRadius: '10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', color: '#fff', border: '1px solid ' + (curTier === t ? 'rgba(168,85,247,.65)' : 'rgba(255,255,255,.12)'), background: curTier === t ? 'rgba(168,85,247,.24)' : 'rgba(255,255,255,.04)' }}>${lab}</button>`)}
          </div>
          ${item.tier != null ? html`<button type="button" onClick=${() => { item.tier = null; force((n) => n + 1); }} className="zp-muted2" style=${{ marginTop: '9px', fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>↺ Reset to category default</button>` : html`<div className="zp-muted2" style=${{ marginTop: '9px', fontSize: '11px' }}>Auto — follows the category default from Finance › Service.</div>`}
        </div>
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Staff</span><span className="zp-card-v">${item.staff == null ? 'Auto (pool)' : item.staff + ' assigned'}</span></div>
          <div className="zp-muted2" style=${{ fontSize: '12px', marginBottom: '10px' }}>Assign dedicated staff to this building instead of the category pool. More staff → higher coverage → more sales & happiness; each costs $70/day.</div>
          <div style=${{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button type="button" onClick=${() => { const c = item.staff == null ? 0 : item.staff; item.staff = Math.max(0, c - 1); force((n) => n + 1); }} style=${stepBtn}>−</button>
            <span style=${{ minWidth: '42px', textAlign: 'center', fontWeight: 700, fontSize: '15px' }}>${item.staff == null ? '—' : item.staff}</span>
            <button type="button" onClick=${() => { const c = item.staff == null ? 0 : item.staff; item.staff = Math.min(5, c + 1); force((n) => n + 1); }} style=${stepBtn}>+</button>
            ${item.staff != null ? html`<button type="button" onClick=${() => { item.staff = null; force((n) => n + 1); }} className="zp-muted2" style=${{ marginLeft: 'auto', fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>↺ Auto</button>` : ''}
          </div>
          <div className="zp-muted2" style=${{ fontSize: '11px', marginTop: '9px' }}>Coverage ${Math.round((eco ? eco.buildingCoverage(item) : 0) * 100)}%</div>
        </div>
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Status</span><span className="zp-card-v">$${income.toLocaleString('en-US')}/day</span></div>
          <button type="button" onClick=${() => { item.closed = !item.closed; force((n) => n + 1); }} className="zp-listbtn"
            style=${{ background: item.closed ? 'rgba(251,113,133,.14)' : 'rgba(52,211,153,.12)', borderColor: item.closed ? 'rgba(251,113,133,.4)' : 'rgba(52,211,153,.35)' }}>
            <span>${item.closed ? '⛔ Closed' : '✅ Open'}</span><span className="zp-lb-right zp-muted" style=${{ fontSize: '11px' }}>tap to ${item.closed ? 'reopen' : 'close'}</span></button>
        </div>
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Name</span></div>
          <input value=${item.name || ''} placeholder=${def.label} onInput=${(e) => { item.name = e.target.value.slice(0, 24); force((n) => n + 1); }}
            style=${{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: '10px', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', color: '#fff', fontSize: '13px' }} />
        </div>
        <button type="button" onClick=${() => bus.emit('economy:catApplyFrom', { type: item.type, tier: curTier, staff: item.staff })}
          className="zp-listbtn" style=${{ background: 'rgba(47,111,237,.14)', borderColor: 'rgba(95,162,255,.4)' }}>
          <span>📋 Apply to whole category</span><span className="zp-lb-right zp-muted" style=${{ fontSize: '11px' }}>sets the Finance default</span></button>
      `}
    <//>
  `;
}

// Habitat panel: click an enclosure to inspect welfare, space/social, disease, bloodline — and release
// pure adults to the wild (conservation). Polls the live sim while open.
function HabitatPanel({ id, onClose }) {
  const [, force] = useState(0);
  useEffect(() => { const t = setInterval(() => force((n) => n + 1), 700); return () => clearInterval(t); }, []);
  const w = window.__world;
  const info = (w && w.animals && id != null) ? w.animals.enclosureInfo(id) : null;
  const tone = (v) => v >= 70 ? '#34d399' : v >= 45 ? '#fbbf24' : '#fb7185';
  return html`
    <${SidePanel} icon="🏕️" title=${info ? 'Habitat #' + id : 'Habitat'} subtitle="Welfare · space · disease · bloodline"
      accent=${['#0e7490', '#22d3ee']} onClose=${onClose}>
      ${!info ? html`<div className="zp-muted" style=${{ padding: '20px 4px', fontSize: '13px' }}>Click a fenced enclosure on the map to inspect it.</div>` : html`
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Welfare</span><span className="zp-card-v" style=${{ color: tone(info.avgWell) }}>${info.avgWell}%</span></div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '7px', fontSize: '13px' }}>
            <div style=${{ display: 'flex' }}><span className="zp-muted">Animals</span><span className="zp-num" style=${{ marginLeft: 'auto' }}>${info.count}</span></div>
            <div style=${{ display: 'flex' }}><span className="zp-muted">Space</span><span className="zp-num" style=${{ marginLeft: 'auto', color: info.spaceOk ? '#34d399' : '#fb7185' }}>${info.size} / ${info.spaceNeed} needed</span></div>
            <div style=${{ display: 'flex' }}><span className="zp-muted">Sick</span><span className="zp-num" style=${{ marginLeft: 'auto', color: info.sick ? '#fb7185' : '#34d399' }}>${info.sick}</span></div>
            ${info.sick ? html`<button type="button" onClick=${() => bus.emit('animals:treatEnc', id)} className="zp-listbtn" style=${{ marginTop: '6px', background: 'rgba(56,189,248,.13)', borderColor: 'rgba(56,189,248,.4)' }}><span>🚑 Dispatch vet team</span><span className="zp-lb-right zp-muted" style=${{ fontSize: '11px' }}>$${(info.sick * (store.get().kidsMode ? 130 : 260)).toLocaleString('en-US')}</span></button>` : ''}
          </div>
        </div>
        ${info.species.length === 0 ? html`<div className="zp-muted" style=${{ padding: '8px 4px', fontSize: '13px' }}>No animals here yet — add some from the Animals shop.</div>` : ''}
        ${info.species.map((sp) => html`
          <div key=${sp.key} className="zp-card">
            <div className="zp-card-h">
              <span className="zp-dot"></span>
              <span className="zp-card-t">${sp.label}${sp.rare ? ' ★' : ''}${sp.morphs ? ' ✨' + sp.morphs : ''}</span>
              <span className="zp-card-v" style=${{ color: tone(sp.well) }}>${sp.well}%</span>
            </div>
            <div style=${{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: '12px' }}>
              <span className="zp-muted">${sp.n} · ${sp.young} young</span>
              <span style=${sp.n < sp.socialMin ? { color: '#fbbf24' } : { color: 'rgba(255,255,255,.56)' }}>social ${sp.n}/${sp.socialMin}</span>
              <span style=${{ color: sp.purity < 60 ? '#fb7185' : sp.purity < 80 ? '#fbbf24' : '#34d399' }}>🧬 ${sp.purity}%</span>
              ${sp.sick ? html`<span style=${{ color: '#fb7185' }}>🤒 ${sp.sick} sick</span>` : ''}
            </div>
            ${sp.canRelease ? html`<button type="button" onClick=${() => bus.emit('animal:release', sp.key)} className="zp-listbtn" style=${{ marginTop: '10px', background: 'rgba(52,211,153,.14)', borderColor: 'rgba(52,211,153,.4)' }}>
              <span>🕊️ Release to wild</span><span className="zp-lb-right zp-muted" style=${{ fontSize: '11px' }}>grant + prestige</span></button>` : ''}
          </div>`)}
      `}
    <//>
  `;
}

// Insight overlays: one inspect mode, swappable data lenses. Picking a lens recolors the map to
// answer one question; the active lens shows a live summary + legend. (Lens catalogue in overlays.js.)
function OverlaysPanel({ onClose }) {
  const s = useGameState();
  const active = s.overlayLens || null;
  const [summary, setSummary] = useState('');
  useEffect(() => {
    const off = bus.on('overlay:info', ({ lens, summary }) => { if (lens === store.get().overlayLens) setSummary(summary); });
    return off;
  }, []);
  useEffect(() => { setSummary(''); }, [active]);
  const pick = (id) => store.set({ overlayLens: active === id ? null : id });
  return html`
    <${SidePanel} icon="🔍" title="Insight" subtitle="Pick a lens — one question at a time"
      accent=${['#0ea5b7', '#22d3ee']} onClose=${onClose}>
      ${LENSES.map((L) => {
        const on = active === L.id;
        return html`
        <div key=${L.id} style=${{ marginBottom: '7px' }}>
          <button type="button" onClick=${() => pick(L.id)} className="zp-trow"
            style=${on ? { background: 'rgba(34,211,238,.12)', borderColor: 'rgba(34,211,238,.4)' } : {}}>
            <span className="zp-ti">${L.icon}</span>
            <span style=${{ flex: 1, textAlign: 'left', minWidth: 0 }}>
              <span className="zp-tt" style=${{ display: 'block' }}>${L.label}</span>
              <span className="zp-td" style=${{ display: 'block' }}>${L.q}</span>
            </span>
            <span className=${'zp-sw' + (on ? ' on' : '')}></span>
          </button>
          ${on ? html`
            <div style=${{ padding: '9px 10px 5px 52px' }}>
              <div style=${{ fontSize: '12.5px', fontWeight: 700, color: '#fff', marginBottom: '9px', letterSpacing: '.01em' }}>${summary || '…'}</div>
              <div style=${{ display: 'flex', flexWrap: 'wrap', gap: '8px 15px' }}>
                ${L.legend.map(([k, lab]) => html`
                  <span key=${lab} style=${{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '11px', color: 'rgba(255,255,255,.55)', fontWeight: 600 }}>
                    <span style=${{ width: '11px', height: '11px', borderRadius: '3px', background: TONE[k], boxShadow: `0 0 9px ${TONE[k]}66` }}></span>${lab}
                  </span>`)}
              </div>
            </div>` : ''}
        </div>`;
      })}
      <div className="zp-muted2" style=${{ fontSize: '11px', padding: '6px 8px 2px' }}>One lens at a time keeps the map readable. Tap the active lens again to turn it off.</div>
    <//>
  `;
}

// Zoo TV: media screen with the full news feed as broadcast items.
function ZooTV({ feed, onClose }) {
  const latest = feed[0];
  const title = html`<span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse"></span> Zoo TV — Live</span>`;
  return html`
    <${SidePanel} icon="📺" title=${title} subtitle="Breaking news from your zoo"
      accent=${['#dc2626', '#f87171']} onClose=${onClose} noPad=${true}>
      <div className="px-5 py-4 bg-gradient-to-b from-sky-900/60 to-slate-900 min-h-[90px]">
        ${latest ? html`
          <div className="text-[10px] uppercase tracking-widest text-red-300 mb-1">Breaking</div>
          <div className="text-lg font-bold leading-snug">${latest.headline}</div>
          <div className="text-[10px] text-white/40 mt-1">Day ${latest.day} · Year ${latest.year}</div>
        ` : html`<div className="text-white/40 text-sm">No news yet — go build something!</div>`}
      </div>
      <div className="divide-y divide-white/5">
        ${feed.slice(1, 20).map((n) => html`
          <div key=${n.id} className="px-5 py-2 flex items-start gap-2 text-xs">
            <span className="shrink-0 mt-0.5">${n.kind === 'success' ? '🟢' : n.kind === 'error' ? '🔴' : '⚪'}</span>
            <span className=${n.kind === 'success' ? 'text-emerald-200' : n.kind === 'error' ? 'text-red-200' : 'text-white/70'}>${n.headline}</span>
            <span className="ml-auto shrink-0 text-white/30">D${n.day} Y${n.year}</span>
          </div>
        `)}
      </div>
    <//>
  `;
}

function SavePanel({ onClose }) {
  const [, force] = useState(0);
  useEffect(() => bus.on('ui:toast', (t) => { if (t && /saved/i.test(t.text)) force((x) => x + 1); }), []);
  const slots = listSlots();
  return html`
    <${SidePanel} icon="💾" title="Save Game" subtitle="Store your progress in a slot"
      accent=${['#4f46e5', '#818cf8']} onClose=${onClose}>
      <div style=${{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        ${Array.from({ length: SLOT_COUNT }, (_, i) => {
          const meta = slots[i];
          return html`
            <button key=${i} type="button" onClick=${() => bus.emit('game:save', i)} className="zp-listbtn" style=${{ padding: '12px 14px' }}>
              <span style=${{ fontSize: '22px' }}>${meta ? '💾' : '➕'}</span>
              <span style=${{ flex: 1, minWidth: 0 }}>
                <span style=${{ display: 'block', fontSize: '14px', fontWeight: 600 }}>Slot ${i + 1}</span>
                <span className="zp-muted2 truncate" style=${{ display: 'block', fontSize: '11.5px' }}>${meta ? meta.zooName + ' · Y' + meta.year + ' · $' + meta.money.toLocaleString('en-US') : 'Empty — click to save here'}</span>
              </span>
              <span style=${{ marginLeft: 'auto', fontSize: '11px', fontWeight: 700, padding: '4px 9px', borderRadius: '8px', color: meta ? '#fbbf24' : '#34d399', background: meta ? 'rgba(251,191,36,.14)' : 'rgba(52,211,153,.14)', border: '1px solid ' + (meta ? 'rgba(251,191,36,.3)' : 'rgba(52,211,153,.3)') }}>${meta ? 'Overwrite' : 'Save'}</span>
            </button>`;
        })}
      </div>
      <div className="zp-muted2" style=${{ fontSize: '11.5px', marginTop: '4px', lineHeight: 1.5 }}>The game also autosaves every few days. You can load any save from the main menu.</div>
    <//>
  `;
}

function VisitorsPanel({ onClose }) {
  const [, force] = useState(0);
  useEffect(() => { const t = setInterval(() => force((x) => x + 1), 1500); return () => clearInterval(t); }, []);
  const w = window.__world;
  if (!w) return null;
  const vs = w.visitors.visitors;
  const n = vs.length;
  const attendance = Math.round(vs.reduce((s, v) => s + (v.groupSize || 1) * (v.mult || 1), 0));
  const avgHappy = n ? Math.round(vs.reduce((s, v) => s + v.happiness, 0) / n) : 0;
  const vips = vs.filter((v) => v.vip).length;
  const wishing = vs.filter((v) => v.wish).length;
  const wishDone = vs.filter((v) => v.wish && v.wishDone).length;
  const TYPES = [['family', '👪', 'Family'], ['photographer', '📷', 'Photographer'], ['foodie', '🍔', 'Foodie'], ['naturalist', '🔭', 'Naturalist']];
  const typeCount = {}; vs.forEach((v) => { const id = (v.vtype && v.vtype.id) || 'family'; typeCount[id] = (typeCount[id] || 0) + 1; });
  const NEEDS = [['hunger', '🍔', 'Hunger'], ['thirst', '🥤', 'Thirst'], ['toilet', '🚻', 'Toilet'], ['fun', '🎡', 'Fun']];
  const needAvg = {}; NEEDS.forEach(([k]) => { needAvg[k] = n ? Math.round(vs.reduce((s, v) => s + ((v.needs && v.needs[k]) || 0), 0) / n) : 0; });
  const topNeed = NEEDS.map(([k, , label]) => [label, needAvg[k]]).sort((a, b) => b[1] - a[1])[0];
  const happyFace = avgHappy >= 70 ? '😀' : avgHappy >= 45 ? '😐' : '😟';
  const happyColor = avgHappy >= 70 ? '#34d399' : avgHappy >= 45 ? '#fbbf24' : '#fb7185';
  const q = w.economy ? Math.round(w.economy.serviceQuality() * 100) : 100;
  return html`
    <${SidePanel} icon="🧑‍🤝‍🧑" title="Visitors" subtitle="Who's in your zoo right now"
      accent=${['#db2777', '#f472b6']} onClose=${onClose}>
      ${n === 0 ? html`<div className="zp-muted" style=${{ fontSize: '13px', lineHeight: 1.5, marginBottom: '12px' }}>No visitors right now. Make sure a path connects the entrance gate so guests can come in.</div><${ServiceBreakdown} />` : html`
        <div className="zp-card">
          <div style=${{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div><div className="zp-card-t">In the park</div><div className="zp-num" style=${{ fontSize: '26px', fontWeight: 800, marginTop: '2px' }}>${attendance}</div></div>
            <div style=${{ width: '1px', alignSelf: 'stretch', background: 'rgba(255,255,255,.1)' }}></div>
            <div style=${{ flex: 1 }}><div className="zp-card-t">Avg happiness</div><div style=${{ fontSize: '26px', fontWeight: 800, marginTop: '2px', color: happyColor }}>${happyFace} ${avgHappy}%</div></div>
            ${vips > 0 ? html`<div style=${{ textAlign: 'right' }}><div className="zp-card-t">VIPs</div><div className="zp-num" style=${{ fontSize: '26px', fontWeight: 800, marginTop: '2px', color: '#fbbf24' }}>⭐ ${vips}</div></div>` : ''}
          </div>
        </div>
        <${QualityCard} q=${q} />
        <${ServiceBreakdown} />
        ${(() => {
          const st = (window.__world && window.__world.animals) ? window.__world.animals.starList() : [];
          if (!st.length) return '';
          return html`<div className="zp-card">
            <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Star attractions</span><span className="zp-card-v">${st.length}</span></div>
            <div style=${{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              ${st.map((sa, i) => html`<span key=${i} style=${{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 700, padding: '4px 9px', borderRadius: '999px', background: 'rgba(232,194,74,.14)', border: '1px solid rgba(232,194,74,.4)', color: '#fff' }}>⭐ ${sa.name} <span className="zp-muted" style=${{ fontWeight: 500 }}>${sa.morph ? sa.morph + ' ' : ''}${sa.label}</span></span>`)}
            </div>
          </div>`;
        })()}
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Visitor mix</span></div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            ${TYPES.map(([id, icon, label]) => { const c = typeCount[id] || 0; const pct = n ? Math.round(c / n * 100) : 0; return html`
              <div key=${id} style=${{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style=${{ width: '22px', fontSize: '18px', textAlign: 'center' }}>${icon}</span>
                <span style=${{ width: '104px', fontSize: '13px' }}>${label}</span>
                <div className="zp-bar" style=${{ flex: 1 }}><i style=${{ width: pct + '%', background: 'linear-gradient(90deg,#db2777,#f472b6)' }}></i></div>
                <span className="zp-num zp-muted" style=${{ width: '26px', textAlign: 'right', fontSize: '12.5px' }}>${c}</span>
              </div>`; })}
          </div>
        </div>
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Needs</span><span className="zp-card-v">most wanted: ${topNeed[0]}</span></div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            ${NEEDS.map(([k, icon, label]) => { const val = needAvg[k]; const cls = val >= 66 ? ' red' : val >= 40 ? ' amber' : ''; return html`
              <div key=${k} style=${{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style=${{ width: '22px', fontSize: '16px', textAlign: 'center' }}>${icon}</span>
                <span style=${{ width: '70px', fontSize: '13px' }}>${label}</span>
                <div className=${'zp-bar' + cls} style=${{ flex: 1 }}><i style=${{ width: val + '%' }}></i></div>
                <span className="zp-num zp-muted" style=${{ width: '34px', textAlign: 'right', fontSize: '12.5px' }}>${val}%</span>
              </div>`; })}
          </div>
          <div className="zp-muted2" style=${{ fontSize: '11px', marginTop: '8px', lineHeight: 1.5 }}>Higher bars = more unmet. Build food stands, drink stalls or restrooms to bring them down.</div>
        </div>
        ${wishing > 0 ? html`
          <div className="zp-card">
            <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Animal wishes</span></div>
            <div style=${{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="zp-bar" style=${{ flex: 1 }}><i style=${{ width: (wishing ? Math.round(wishDone / wishing * 100) : 0) + '%' }}></i></div>
              <span className="zp-num zp-muted" style=${{ fontSize: '12.5px', whiteSpace: 'nowrap' }}>${wishDone}/${wishing} seen</span>
            </div>
            <div className="zp-muted2" style=${{ fontSize: '11px', marginTop: '8px', lineHeight: 1.5 }}>Guests hoping to spot a specific species. More variety means more fulfilled wishes.</div>
          </div>
        ` : ''}
      `}
    <//>
  `;
}

function ServiceBreakdown() {
  const [, force] = useState(0);
  useEffect(() => { const t = setInterval(() => force((n) => n + 1), 900); return () => clearInterval(t); }, []);
  const eco = window.__world && window.__world.economy;
  if (!eco) return null;
  const b = eco.qualityBreakdown();
  const pct = (v) => Math.round(v * 100);
  const tone = (v) => v >= 0.7 ? '#34d399' : v >= 0.45 ? '#fbbf24' : '#fb7185';
  const bar = (label, v) => html`
    <div key=${label} style=${{ marginBottom: '8px' }}>
      <div style=${{ display: 'flex', fontSize: '12px', marginBottom: '3px', gap: '8px' }}><span className="zp-muted" style=${{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>${label}</span><span style=${{ marginLeft: 'auto', fontWeight: 700, color: tone(v), flex: '0 0 auto' }}>${pct(v)}%</span></div>
      <div className="zp-bar"><i style=${{ width: pct(v) + '%', background: tone(v) }}></i></div>
    </div>`;
  const staffRow = (r) => {
    const ok = r.have >= r.need;
    return html`<div key=${r.label} style=${{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '5px 0' }}>
      <span style=${{ width: '9px', height: '9px', borderRadius: '50%', background: ok ? '#34d399' : '#fb7185', flex: '0 0 auto' }}></span>
      <span style=${{ fontWeight: 600 }}>${r.label}</span>
      <span className="zp-muted2" style=${{ fontSize: '10.5px' }}>${r.note}</span>
      <span style=${{ marginLeft: 'auto', fontWeight: 700, color: ok ? '#34d399' : '#fb7185' }}>${r.have}/${r.need}${ok ? '' : ' · add ' + (r.need - r.have)}</span>
    </div>`;
  };
  const head = (txt) => html`<div className="zp-card-t" style=${{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.04em', opacity: 0.7, margin: '10px 0 6px' }}>${txt}</div>`;
  return html`
    <div className="zp-card">
      <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">What's missing</span></div>
      <div className="zp-muted2" style=${{ fontSize: '11px', marginBottom: '10px', lineHeight: 1.5 }}>Service quality is the weighted mix below — the lowest bars are what's holding it back.</div>
      ${b.quality_parts.map((p) => bar(p.label + ' · ' + p.w + '%', p.val))}
      ${head('Staff')}
      ${b.staff.map(staffRow)}
      ${head('Comfort coverage')}
      ${b.comfort.map((c) => bar(c.label, c.val))}
      ${head('Need coverage')}
      ${b.needs.map((c) => bar(c.label, c.val))}
    </div>`;
}

function SettingsPanel({ onClose }) {
  const s = useGameState();
  const g = s.graphics || {};
  const setG = (patch) => { store.set({ graphics: { ...(store.get().graphics || {}), ...patch } }); bus.emit('gfx:apply'); };
  const seg = (opts, val, onPick) => html`
    <div style=${{ display: 'flex', gap: '6px' }}>
      ${opts.map(([v, label]) => html`<button key=${label} type="button" onClick=${() => onPick(v)}
        style=${{ flex: 1, padding: '9px 4px', borderRadius: '10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', color: '#fff', border: '1px solid ' + (val === v ? 'rgba(95,162,255,.6)' : 'rgba(255,255,255,.12)'), background: val === v ? 'rgba(47,111,237,.24)' : 'rgba(255,255,255,.04)' }}>${label}</button>`)}
    </div>`;
  const fps = (window.__world && window.__world.renderer) ? window.__world.renderer.fps : null;
  return html`
    <${SidePanel} icon="⚙️" title="Settings" subtitle="Graphics & performance" accent=${['#475569', '#94a3b8']} onClose=${onClose}>
      <div className="zp-card">
        <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Graphics quality</span>${fps != null ? html`<span className="zp-card-v">${fps} fps</span>` : ''}</div>
        <div className="zp-muted2" style=${{ fontSize: '11.5px', marginBottom: '10px', lineHeight: 1.5 }}>Auto adapts the resolution, shadows and detail to keep the framerate smooth. Pick a level to lock it.</div>
        ${seg([[true, 'Auto'], [false, 'Manual']], g.auto !== false, (v) => setG({ auto: v }))}
        ${g.auto === false ? html`<div style=${{ marginTop: '10px' }}>${seg([[2, 'High'], [1, 'Medium'], [0, 'Fast']], g.quality == null ? 2 : g.quality, (v) => setG({ quality: v }))}</div>` : ''}
      </div>
      <div className="zp-card">
        <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Frame rate</span></div>
        <div className="zp-muted2" style=${{ fontSize: '11.5px', marginBottom: '10px', lineHeight: 1.5 }}>Cap the framerate for smoother, steadier pacing — a locked 30 often feels better than 60 with dips, and frees headroom on weaker GPUs.</div>
        ${seg([[30, '30'], [60, '60'], [120, '120'], [0, 'Max']], g.fpsCap == null ? 60 : g.fpsCap, (v) => setG({ fpsCap: v }))}
      </div>
      <div className="zp-card">
        <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Shadows</span></div>
        <div className="zp-muted2" style=${{ fontSize: '11.5px', marginBottom: '10px', lineHeight: 1.5 }}>Shadow sharpness. Lower is much lighter on weaker GPUs — with the cartoon look the difference is subtle.</div>
        ${seg([[0, 'Off'], [128, '128'], [256, '256'], [512, '512'], [1024, '1024'], [2048, '2048']], g.shadowRes == null ? 512 : g.shadowRes, (v) => setG({ shadowRes: v }))}
      </div>
      <div className="zp-card">
        <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Tree density</span><span className="zp-card-v">${Math.round((g.treeDensity == null ? 1 : g.treeDensity) * 100)}%</span></div>
        <div className="zp-muted2" style=${{ fontSize: '11.5px', marginBottom: '10px', lineHeight: 1.5 }}>Thin out the forests for more fps on big maps. Trees you've cleared for paths & buildings stay gone.</div>
        <input type="range" min="0.2" max="1" step="0.1" value=${g.treeDensity == null ? 1 : g.treeDensity} onInput=${(e) => setG({ treeDensity: +e.target.value })} style=${{ width: '100%' }} />
      </div>
      <div className="zp-card">
        <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Sound</span></div>
        <div className="zp-muted2" style=${{ fontSize: '11.5px', marginBottom: '10px', lineHeight: 1.5 }}>Gentle ambient soundscape — soft wind and the occasional birdsong.</div>
        ${seg([[false, 'On'], [true, 'Off']], !!g.muted, (v) => { setG({ muted: v }); bus.emit('audio:muted', v); })}
      </div>
      <div className="zp-card">
        <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Performance stats</span></div>
        <div className="zp-muted2" style=${{ fontSize: '11.5px', marginBottom: '10px', lineHeight: 1.5 }}>Show a live FPS / frame-time / 1%-low / draw-call overlay to spot bottlenecks.</div>
        ${seg([[false, 'Off'], [true, 'On']], !!g.showStats, (v) => setG({ showStats: v }))}
      </div>
    <//>
  `;
}

function QualityCard({ q, hint }) {
  const cls = q >= 70 ? '' : q >= 45 ? ' amber' : ' red';
  const col = q >= 70 ? 'zp-pos' : q >= 45 ? 'zp-warn' : 'zp-neg';
  return html`
    <div className="zp-card">
      <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Service quality</span><span className=${'zp-card-v ' + col}>${q}%</span></div>
      <div className=${'zp-bar' + cls}><i style=${{ width: q + '%' }}></i></div>
      ${hint ? html`<div className="zp-muted2" style=${{ fontSize: '11px', marginTop: '8px', lineHeight: 1.5 }}>${hint}</div>` : ''}
    </div>
  `;
}

function FinancePanel({ s, onClose }) {
  const offer = s.contractOffer;
  const ok = offer && offer.purity >= offer.reqPurity;
  const f = s.finance || {};
  const fp = s.financePrev || {};
  const inc = (f.admission || 0) + (f.shops || 0) + (f.contracts || 0) + (f.grants || 0);
  const exp = (f.wages || 0) + (f.upkeep || 0) + (f.interest || 0) + (f.permit || 0) + (f.fines || 0);
  const net = inc - exp;
  const netPrev = ((fp.admission || 0) + (fp.shops || 0) + (fp.contracts || 0) + (fp.grants || 0)) - ((fp.wages || 0) + (fp.upkeep || 0) + (fp.interest || 0) + (fp.permit || 0) + (fp.fines || 0));
  const q = Math.round((s.serviceQuality == null ? 1 : s.serviceQuality) * 100);
  const [tab, setTab] = useState('income');
  const tabs = html`<${TabRow} active=${tab} onSelect=${setTab} tabs=${[['income', 'Income'], ['expenses', 'Expenses'], ['growth', 'Growth'], ['service', 'Service']]} />`;
  return html`
    <${SidePanel} icon="💰" title="Finance" subtitle="Income · expenses · growth"
      accent=${['#2f6fed', '#5fa2ff']} onClose=${onClose} tabs=${tabs}>
      ${tab === 'income' ? html`
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Revenue today</span><span className="zp-card-v zp-pos">$${inc.toLocaleString('en-US')}</span></div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            ${[['Admission', f.admission || 0], ['Shops', f.shops || 0], ['Contracts', f.contracts || 0], ['Grants', f.grants || 0]].map(([l, v]) => html`<div key=${l} style=${{ display: 'flex', fontSize: '13px' }}><span className="zp-muted">${l}</span><span className="zp-num" style=${{ marginLeft: 'auto' }}>$${v.toLocaleString('en-US')}</span></div>`)}
          </div>
        </div>
        <${QualityCard} q=${q} hint="Driven by each building's service tier & staffing, cleanliness, and enough outlets for the crowd. Higher quality lifts sales and guest happiness." />
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Admission</span><span className="zp-card-v">$${s.entranceFee} / guest</span></div>
          <input type="range" min="0" max="80" step="5" value=${s.entranceFee}
            onChange=${(e) => bus.emit('economy:setFee', +e.target.value)} className="zp-range" />
          <div className="zp-muted2" style=${{ fontSize: '12px' }}>Higher fees earn more per guest but deter visitors.</div>
        </div>
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Breeding contracts</span></div>
          ${offer ? html`
            <div className="zp-contract">
              <div style=${{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '8px' }}>
                <span style=${{ fontSize: '20px' }}>📜</span>
                <span style=${{ fontSize: '14px', fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>${offer.zoo}</span>
                <span className="zp-pos" style=${{ fontSize: '14px', fontWeight: 800 }}>+$${offer.reward.toLocaleString('en-US')}</span>
              </div>
              <div className="zp-muted" style=${{ fontSize: '12.5px', marginBottom: '10px' }}>Ship <b style=${{ color: '#eef2f6' }}>${offer.need} ${offer.label}</b> with bloodline ≥ ${offer.reqPurity}% — keep your breeding core.</div>
              <div style=${{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span className="zp-muted2" style=${{ fontSize: '11.5px' }}>Your bloodline</span>
                <div className=${'zp-bar' + (ok ? '' : ' amber')} style=${{ flex: 1 }}><i style=${{ width: offer.purity + '%' }}></i></div>
                <span className=${(ok ? 'zp-pos' : 'zp-warn') + ' zp-num'} style=${{ fontSize: '12px', fontWeight: 700 }}>${offer.purity}%</span>
              </div>
              <div className="zp-btn-row">
                <button disabled=${!ok} onClick=${() => bus.emit('contract:accept')} className="zp-btn zp-btn-primary">Fulfil +$${offer.reward.toLocaleString('en-US')}</button>
                <button onClick=${() => bus.emit('contract:decline')} className="zp-btn" style=${{ flex: '0 0 auto' }}>Decline</button>
              </div>
            </div>
          ` : html`<div className="zp-muted" style=${{ fontSize: '12px', lineHeight: 1.5 }}>No active contract right now. Rival zoos commission animals of a strong bloodline every couple of weeks — keep your genetics pure to cash in.</div>`}
        </div>
      ` : tab === 'expenses' ? html`
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Costs today</span><span className="zp-card-v zp-neg">$${exp.toLocaleString('en-US')}</span></div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            ${[['Wages', f.wages || 0], ['Upkeep', f.upkeep || 0], ['Interest', f.interest || 0], ['Permit', f.permit || 0], ['Fines', f.fines || 0]].map(([l, v]) => html`<div key=${l} style=${{ display: 'flex', fontSize: '13px' }}><span className="zp-muted">${l}</span><span className="zp-num" style=${{ marginLeft: 'auto' }}>$${v.toLocaleString('en-US')}</span></div>`)}
          </div>
          <div style=${{ display: 'flex', alignItems: 'center', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,.08)' }}>
            <span className="zp-card-t">Net / day</span>
            <span className=${'zp-num ' + (net >= 0 ? 'zp-pos' : 'zp-neg')} style=${{ marginLeft: 'auto', fontSize: '18px', fontWeight: 800 }}>${net >= 0 ? '+' : '−'}$${Math.abs(net).toLocaleString('en-US')}</span>
          </div>
          <div className="zp-muted2" style=${{ fontSize: '11px', marginTop: '6px' }}>Yesterday: ${netPrev >= 0 ? '+' : '−'}$${Math.abs(netPrev).toLocaleString('en-US')} net</div>
        </div>
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Staff</span></div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            ${Object.entries(STAFF).map(([role, def]) => html`
              <div key=${role} style=${{ display: 'flex', alignItems: 'center' }}>
                <div><div style=${{ fontSize: '14px' }}>${def.label}s</div><div className="zp-muted2" style=${{ fontSize: '11.5px' }}>$${def.wage}/day each</div></div>
                <div className="zp-stepper" style=${{ marginLeft: 'auto' }}>
                  <button onClick=${() => bus.emit('economy:hire', { role, delta: -1 })}>−</button>
                  <span className="zp-val">${s['staff_' + role]}</span>
                  <button onClick=${() => bus.emit('economy:hire', { role, delta: 1 })}>+</button>
                  <button onClick=${() => bus.emit('economy:hire', { role, delta: 10 })}>+10</button>
                </div>
              </div>`)}
          </div>
        </div>
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Loans</span>${s.loan > 0 ? html`<span className="zp-card-v zp-neg">owing $${s.loan.toLocaleString('en-US')}</span>` : ''}</div>
          <div className="zp-btn-row" style=${{ marginBottom: s.loan > 0 ? '8px' : 0 }}>
            ${LOAN_OPTIONS.map((o) => html`<button key=${o.amount} onClick=${() => bus.emit('economy:takeLoan', o)} className="zp-btn zp-num">+$${(o.amount / 1000)}k</button>`)}
          </div>
          ${s.loan > 0 ? html`<button onClick=${() => bus.emit('economy:repayLoan', 10000)} className="zp-btn" style=${{ width: '100%', color: '#34d399' }}>Repay $10k</button>` : ''}
        </div>
      ` : tab === 'growth' ? html`
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Grand projects</span></div>
          <div className="zp-muted2" style=${{ fontSize: '11.5px', marginBottom: '10px', lineHeight: 1.5 }}>One-time landmark investments — turn late-game cash into lasting prestige & reputation.</div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            ${Object.entries(PROJECTS).map(([id, p]) => { const owned = (s.projects || {})[id]; const afford = s.money >= p.cost; return html`
              <button key=${id} type="button" disabled=${owned || !afford} onClick=${() => bus.emit('project:buy', id)} className="zp-listbtn" style=${{ background: owned ? 'rgba(52,211,153,.13)' : 'rgba(255,255,255,.04)', borderColor: owned ? 'rgba(52,211,153,.4)' : 'rgba(255,255,255,.1)', opacity: (owned || afford) ? 1 : 0.5 }}>
                <span style=${{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.3 }}>
                  <span style=${{ fontWeight: 700 }}>${p.icon} ${p.label}</span>
                  <span className="zp-muted" style=${{ fontSize: '11px' }}>+${p.prestige} prestige · +${p.rep} rep</span>
                </span>
                <span className="zp-lb-right" style=${{ fontWeight: 800, fontSize: '12px', color: owned ? '#34d399' : '#fff' }}>${owned ? '✓ Funded' : '$' + (p.cost / 1000) + 'k'}</span>
              </button>`; })}
          </div>
        </div>
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Marketing</span></div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            ${Object.entries(MARKETING).map(([key, c]) => html`
              <button key=${key} onClick=${() => bus.emit('economy:marketing', key)} className="zp-listbtn">
                <span>${c.label}</span>
                <span className="zp-lb-right zp-num zp-muted">$${c.price.toLocaleString('en-US')} · ${c.days}d</span>
              </button>`)}
          </div>
        </div>
        <div className="zp-card">
          <div className="zp-card-h"><span className="zp-dot"></span><span className="zp-card-t">Research</span></div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            ${Object.entries(RESEARCH).map(([key, def]) => {
              const done = (s.researchDone || []).includes(key);
              const active = s.researchActive && s.researchActive.key === key;
              const locked = def.requires && !(s.researchDone || []).includes(def.requires);
              return html`
                <button key=${key} disabled=${done || active || locked || !!s.researchActive}
                  onClick=${() => bus.emit('research:start', key)} title=${def.effect}
                  className="zp-listbtn" style=${{ color: done ? '#34d399' : active ? '#fbbf24' : locked ? 'rgba(255,255,255,.3)' : undefined }}>
                  <span>${def.label}</span>
                  <span className="zp-lb-right zp-num zp-muted">${done ? '✓ done' : active ? s.researchActive.daysLeft + 'd left' : locked ? '🔒' : '$' + def.price.toLocaleString('en-US') + ' · ' + def.days + 'd'}</span>
                </button>`;
            })}
          </div>
        </div>
      ` : html`
        <div className="zp-muted2" style=${{ fontSize: '11.5px', marginBottom: '2px', lineHeight: 1.5 }}>Staff each building type and set its product tier. Better tiers and full staffing lift sales and guest happiness — at a higher daily cost.</div>
        ${[['food', '🍔', 'Food'], ['drink', '🥤', 'Drinks'], ['fun', '🎡', 'Fun'], ['facility', '🚻', 'Facilities']].map(([id, icon, label]) => {
          const _e = window.__world && window.__world.economy;
          const ci = _e ? { buildings: _e._catBuildings(id), auto: _e._catAutoBuildings(id), custom: _e._catCustom(id), staff: (s.catStaff || {})[id] || 0, tier: ((s.catTier || {})[id] == null ? 1 : (s.catTier || {})[id]), quality: _e.categoryQualityPct(id) } : { buildings: 0, auto: 0, custom: 0, staff: 0, tier: 1, quality: 0 };
          return html`
            <div key=${id} className="zp-card">
              <div className="zp-card-h"><span style=${{ fontSize: '18px' }}>${icon}</span><span className="zp-card-t">${label}</span><span className="zp-card-v">${ci.buildings} building${ci.buildings === 1 ? '' : 's'}</span></div>
              ${ci.buildings === 0 ? html`<div className="zp-muted2" style=${{ fontSize: '11.5px' }}>No ${label.toLowerCase()} buildings placed yet.</div>` : html`
                <div style=${{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <span className="zp-muted2" style=${{ fontSize: '11.5px' }}>Quality</span>
                  <div className=${'zp-bar' + (ci.quality >= 70 ? '' : ci.quality >= 45 ? ' amber' : ' red')} style=${{ flex: 1 }}><i style=${{ width: ci.quality + '%' }}></i></div>
                  <span className=${'zp-num ' + (ci.quality >= 70 ? 'zp-pos' : ci.quality >= 45 ? 'zp-warn' : 'zp-neg')} style=${{ fontSize: '12px', fontWeight: 700, width: '36px', textAlign: 'right' }}>${ci.quality}%</span>
                </div>
                <div style=${{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <div><div style=${{ fontSize: '13px', fontWeight: 600 }}>Staff</div><div className="zp-muted2" style=${{ fontSize: '11px' }}>${ci.staff} for ${ci.auto} on Auto · $70/day each</div></div>
                  <div className="zp-stepper" style=${{ marginLeft: 'auto' }}>
                    <button onClick=${() => bus.emit('economy:catStaff', { cat: id, delta: -1 })}>−</button>
                    <span className="zp-val">${ci.staff}</span>
                    <button onClick=${() => bus.emit('economy:catStaff', { cat: id, delta: 1 })}>+</button>
                  </div>
                </div>
                <div className="zp-tabs">
                  ${['Budget', 'Standard', 'Premium'].map((t, ti) => html`<button key=${t} type="button" onClick=${() => bus.emit('economy:catTier', { cat: id, tier: ti })} className=${'zp-tab' + (ci.tier === ti ? ' active' : '')}>${t}</button>`)}
                </div>
                ${ci.custom > 0 ? html`<div style=${{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '11px', fontSize: '11.5px' }}><span className="zp-warn">${ci.custom} building${ci.custom === 1 ? '' : 's'} customised — ignore this default</span><button type="button" onClick=${() => bus.emit('economy:catClearOverrides', id)} style=${{ marginLeft: 'auto', fontSize: '11px', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.14)', borderRadius: '8px', padding: '4px 9px', cursor: 'pointer', color: '#fff', whiteSpace: 'nowrap' }}>Reset all to Auto</button></div>` : ''}
              `}
            </div>`;
        })}
      `}
    <//>
  `;
}

// Little "+$" pop next to the cash counter whenever a stand/museum makes a sale.
function SaleFlash({ kidsMode }) {
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    let sum = 0, timer = null;
    return bus.on('ui:sale', ({ amount }) => {
      sum += amount;
      setFlash(sum);
      clearTimeout(timer);
      timer = setTimeout(() => { sum = 0; setFlash(null); }, 1500);
    });
  }, []);
  if (flash == null) return null;
  return html`<div className="text-emerald-300 text-xs font-bold animate-pulse">+${kidsMode ? '🪙' + Math.max(1, Math.round(flash / 100)) : '$' + flash}</div>`;
}

// Sticker collection + trophy case.
function StickerBook({ collected, trophies, onClose }) {
  const have = new Set(collected);
  const won = new Set(trophies);
  const owned = { borderColor: 'rgba(52,211,153,.5)', background: 'rgba(52,211,153,.12)' };
  const [tab, setTab] = useState('stickers');
  const tabs = html`<${TabRow} active=${tab} onSelect=${setTab} tabs=${[['stickers', 'Stickers ' + have.size + '/' + Object.keys(SPECIES).length], ['trophies', 'Trophies ' + won.size + '/' + TROPHIES.length]]} />`;
  return html`
    <${SidePanel} icon="📖" title="Collection" subtitle="Animal stickers & trophies"
      accent=${['#ea9214', '#fbbf24']} onClose=${onClose} tabs=${tabs}>
      ${tab === 'stickers' ? html`
        <div className="zp-grid" style=${{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
          ${Object.entries(SPECIES).sort((a, b) => a[1].minPrestige - b[1].minPrestige).map(([key, sp]) => html`
            <div key=${key} className=${'zp-tile' + (have.has(key) ? '' : ' lock')} style=${{ cursor: 'default', ...(have.has(key) ? owned : {}) }}>
              <span className="zp-thumb" style=${{ fontSize: '24px' }}>${have.has(key) ? '🏅' : '❔'}</span>
              <span className="zp-tname">${have.has(key) ? sp.label : '???'}</span>
            </div>
          `)}
        </div>
      ` : html`
        <div className="zp-grid" style=${{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          ${TROPHIES.map((t) => html`
            <div key=${t.id} className=${'zp-tile' + (won.has(t.id) ? '' : ' lock')} style=${{ cursor: 'default', ...(won.has(t.id) ? owned : {}) }}>
              <span style=${{ fontSize: '26px' }}>${t.icon}</span>
              <span className="zp-tname">${t.label}</span>
              <span className="zp-muted2" style=${{ fontSize: '10px', textAlign: 'center', lineHeight: 1.2 }}>${t.desc}</span>
            </div>
          `)}
        </div>
      `}
    <//>
  `;
}

// Animal manager: all animals grouped by species — rename overview, ages, values, sell buttons.
function AnimalManager({ kidsMode, onClose }) {
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 2000); return () => clearInterval(t); }, []);
  const [tab, setTab] = useState('all');
  const w = window.__world;
  if (!w) return null;
  const s = store.get();
  const money = (v) => kidsMode ? '🪙' + Math.max(1, Math.round(v / 100)) : '$' + v.toLocaleString('en-US');
  const groups = {};
  w.animals.animals.forEach((a) => { (groups[a.species] = groups[a.species] || []).push(a); });
  const sellMany = (list) => list.forEach((a) => bus.emit('animal:sell', a.id));
  const entries = Object.entries(groups).filter(([key]) => tab === 'all' || animalCat(key) === tab).sort((a, b) => (a[0] < b[0] ? -1 : 1));
  const tabs = html`<${TabRow} active=${tab} onSelect=${setTab} tabs=${[['all', 'All'], ['predators', 'Predators'], ['prey', 'Prey'], ['other', 'Birds & Pets']]} />`;
  return html`
    <${SidePanel} icon="🧬" title="Genetics Lab" subtitle=${'Bloodlines & breeding stock · ' + w.animals.animals.length + ' animals'}
      accent=${['#0e9e6e', '#34d399']} onClose=${onClose} tabs=${tabs}>
      <div className="zp-card" style=${{ borderColor: 'rgba(96,165,250,.35)', background: 'linear-gradient(180deg, rgba(96,165,250,.10), transparent)' }}>
        <div className="zp-card-h"><span className="zp-dot" style=${{ background: '#60a5fa', boxShadow: '0 0 10px #60a5fa' }}></span><span className="zp-card-t">Government permit</span><span className="zp-card-v">Tier ${s.permitTier || 1}</span></div>
        <div style=${{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <span className="zp-muted2" style=${{ fontSize: '11.5px' }}>Adult animals</span>
          <div className=${'zp-bar' + ((s.adultCount || 0) >= (s.animalCap || 1) ? ' red' : (s.adultCount || 0) >= (s.animalCap || 1) * 0.85 ? ' amber' : '')} style=${{ flex: 1 }}><i style=${{ width: Math.min(100, ((s.adultCount || 0) / (s.animalCap || 1)) * 100) + '%' }}></i></div>
          <span className="zp-num" style=${{ fontWeight: 700, fontSize: '12.5px' }}>${s.adultCount || 0}/${s.animalCap || 0}</span>
        </div>
        ${(() => {
          const cur = PERMIT_TIERS[(s.permitTier || 1) - 1];
          const next = PERMIT_TIERS[(s.permitTier || 1)];
          if (cur && cur.upgrade != null && next) {
            return html`<button onClick=${() => bus.emit('permit:upgrade')} disabled=${s.money < cur.upgrade} className="zp-btn zp-btn-primary" style=${{ width: '100%' }}>Raise to Tier ${(s.permitTier || 1) + 1} · cap ${next.cap} — $${cur.upgrade.toLocaleString('en-US')}</button>`;
          }
          return html`<div className="zp-muted2" style=${{ fontSize: '11.5px', textAlign: 'center' }}>Maximum permit tier reached — cap ${s.animalCap}.</div>`;
        })()}
        <div className="zp-muted2" style=${{ fontSize: '10.5px', marginTop: '8px', lineHeight: 1.4 }}>Only adults count — youngsters are free until they grow up. Over the limit and you'll choose which species to reduce to youngsters.</div>
      </div>
      ${w.animals.animals.length === 0 ? html`<div className="zp-muted" style=${{ fontSize: '13px' }}>No animals yet — buy some in the animal shop!</div>` : entries.length === 0 ? html`<div className="zp-muted" style=${{ fontSize: '13px' }}>No animals in this category yet.</div>` : ''}
      ${entries.map(([key, list]) => {
        const sp = SPECIES[key];
        const sorted = [...list].sort((a, b) => saleValue(b) - saleValue(a));
        const old = list.filter((a) => a.maturity >= 1 && (a.ageDays || 0) > 80);
        const oldTotal = old.reduce((sum, a) => sum + saleValue(a), 0);
        const purity = bloodlinePct(key);
        const males = list.filter((a) => a.sex === 'm').length, females = list.length - males;
        const babies = list.filter((a) => a.maturity < 1).length;
        const morphs = list.filter((a) => a.morph).length;
        const pcol = purity >= 75 ? '#34d399' : purity >= 50 ? '#fbbf24' : '#fb7185';
        return html`
          <div key=${key} className="zp-card">
            <div style=${{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <div style=${{ fontWeight: 700, fontSize: '14px' }}>${sp.label} <span className="zp-muted2" style=${{ fontWeight: 400 }}>×${list.length}</span></div>
              ${old.length > 1 ? html`
                <button onClick=${() => sellMany(old)} className="zp-btn zp-num" style=${{ marginLeft: 'auto', padding: '6px 10px', fontSize: '11.5px', background: 'rgba(245,158,11,.85)', borderColor: 'transparent', color: '#fff' }}>
                  Sell ${old.length} oldest — ${money(oldTotal)}
                </button>
              ` : ''}
            </div>
            <div style=${{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11.5px', marginBottom: '10px' }}>
              <span className="zp-muted">♂ ${males} · ♀ ${females} · 🍼 ${babies}${morphs ? ' · ✨ ' + morphs : ''}</span>
              <span style=${{ flex: 1 }}></span>
              <span className="zp-muted2">🧬</span>
              <div className=${'zp-bar' + (purity >= 75 ? '' : purity >= 50 ? ' amber' : ' red')} style=${{ width: '80px' }}><i style=${{ width: purity + '%' }}></i></div>
              <span className="zp-num" style=${{ width: '34px', textAlign: 'right', fontWeight: 700, color: pcol }}>${purity}%</span>
            </div>
            <div style=${{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              ${sorted.map((a) => html`
                <div key=${a.id} style=${{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.06)', borderRadius: '10px', padding: '7px 11px' }}>
                  <span style=${{ width: '16px' }}>${a.sex === 'f' ? '♀' : '♂'}</span>
                  <span style=${{ flex: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>${a.name || sp.label}</span>
                  <span className="zp-muted2" style=${{ width: '54px' }}>${a.maturity < 1 ? '🍼 baby' : (a.ageDays || 0) + 'd'}</span>
                  <span style=${{ width: '26px', textAlign: 'center', color: a.wellbeing >= 75 ? '#34d399' : a.wellbeing < 40 ? '#fb7185' : 'rgba(255,255,255,.6)' }}>${a.wellbeing >= 75 ? '😀' : a.wellbeing < 40 ? '😢' : '😐'}</span>
                  <button onClick=${() => bus.emit('animal:sell', a.id)} className="zp-btn zp-num" style=${{ padding: '5px 9px', fontSize: '12px', background: 'rgba(245,158,11,.85)', borderColor: 'transparent', color: '#fff', whiteSpace: 'nowrap' }}>🚚 ${money(saleValue(a))}</button>
                </div>
              `)}
            </div>
          </div>
        `;
      })}
      <div className="zp-muted2" style=${{ fontSize: '10.5px' }}>Young + happy animals are worth the most. Values refresh live.</div>
    <//>
  `;
}

// Click an animal → name it. Named animals show up in Zoo News.
function AnimalPanel() {
  const kidsMode = useGameState().kidsMode;
  const [sel, setSel] = useState(null);
  const [name, setName] = useState('');
  useEffect(() => bus.on('animal:selected', (a) => { setSel(a); setName(a.name || ''); }), []);
  if (!sel) return null;
  return html`
    <div className="absolute right-4 glass-dark rounded-2xl px-5 py-4 text-white pointer-events-auto shadow-xl" style=${{ top: '19rem', maxWidth: '16rem' }}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-sm">${sel.name ? sel.name + ' the ' + sel.label.toLowerCase() : 'Name this ' + sel.label.toLowerCase()}</div>
        <button onClick=${() => setSel(null)} className="text-white/40 hover:text-white leading-none">✕</button>
      </div>
      <input
        className="glass w-full rounded-xl px-4 py-2 mt-2 text-white placeholder-white/30 outline-none text-sm"
        placeholder="e.g. Max"
        value=${name}
        maxLength="20"
        onChange=${(e) => setName(e.target.value)}
        onKeyDown=${(e) => { if (e.key === 'Enter') { bus.emit('animal:rename', { id: sel.id, name }); setSel(null); } }}
      />
      <button
        className="w-full rounded-xl py-2 mt-2 bg-emerald-500 hover:bg-emerald-400 transition-colors font-bold text-sm"
        onClick=${() => { bus.emit('animal:rename', { id: sel.id, name }); setSel(null); }}
      >Save name</button>
      <button
        className="w-full rounded-xl py-2 mt-2 bg-amber-500/80 hover:bg-amber-400 transition-colors font-bold text-sm"
        onClick=${() => { bus.emit('animal:sell', sel.id); setSel(null); }}
      >🚚 Move to another zoo — ${kidsMode ? '🪙' + Math.round(sel.value / 100) : '$' + sel.value.toLocaleString('en-US')}</button>
      <div className="text-[10px] text-white/40 mt-1 text-center">${sel.baby ? 'Young animals are worth the most!' : 'Value drops as animals age.'}</div>
    </div>
  `;
}

function Stat({ label, value }) {
  return html`
    <div className="text-center shrink-0">
      <div className="text-[10px] uppercase tracking-wider text-white/40">${label}</div>
      <div className="text-sm font-semibold">${value}</div>
    </div>
  `;
}

function ContractDialog({ offer }) {
  const ok = offer.purity >= offer.reqPurity;
  return html`
    <div className="absolute inset-0 flex items-center justify-center pointer-events-auto" style=${{ zIndex: 60, background: 'rgba(8,12,18,0.6)' }}>
      <div className="glass-dark rounded-3xl text-white shadow-2xl" style=${{ width: '440px', maxWidth: '92vw', padding: '22px' }}>
        <div style=${{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <div style=${{ width: '44px', height: '44px', borderRadius: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', background: 'linear-gradient(135deg,#7c3aed,#a855f7)', boxShadow: '0 4px 14px rgba(168,85,247,.4)', marginBottom: '12px' }}>📜</div>
          <div style=${{ fontSize: '16px', fontWeight: 800, marginBottom: '4px' }}>Breeding contract — ${offer.zoo}</div>
          <div className="zp-muted" style=${{ fontSize: '13px', lineHeight: 1.5, marginBottom: '14px' }}>${offer.zoo} will pay <b style=${{ color: '#fff' }}>$${offer.reward.toLocaleString('en-US')}</b> for <b style=${{ color: '#fff' }}>${offer.need} ${offer.label}</b> with a bloodline purity of at least <b style=${{ color: '#fff' }}>${offer.reqPurity}%</b>. You keep your breeding core.</div>
        </div>
        <div style=${{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 13px', borderRadius: '12px', background: ok ? 'rgba(52,211,153,.12)' : 'rgba(251,191,36,.12)', border: '1px solid ' + (ok ? 'rgba(52,211,153,.35)' : 'rgba(251,191,36,.35)'), marginBottom: '16px', fontSize: '12px', fontWeight: 700 }}>
          <span>🧬 ${offer.label} bloodline</span>
          <span style=${{ color: ok ? '#34d399' : '#fbbf24' }}>${offer.purity}% ${ok ? '· meets it' : '· too low'}</span>
        </div>
        <div style=${{ display: 'flex', gap: '10px' }}>
          <button onClick=${() => bus.emit('contract:accept')} disabled=${!ok} style=${{ flex: 1, padding: '12px', borderRadius: '12px', fontWeight: 800, fontSize: '13px', border: 'none', cursor: ok ? 'pointer' : 'not-allowed', color: '#fff', background: ok ? 'linear-gradient(135deg,#059669,#34d399)' : 'rgba(255,255,255,.08)', opacity: ok ? 1 : 0.5 }}>Fulfil +$${offer.reward.toLocaleString('en-US')}</button>
          <button onClick=${() => bus.emit('contract:decline')} style=${{ padding: '12px 18px', borderRadius: '12px', fontWeight: 700, fontSize: '13px', border: '1px solid rgba(255,255,255,.14)', cursor: 'pointer', color: '#fff', background: 'rgba(255,255,255,.06)' }}>Decline</button>
        </div>
      </div>
    </div>`;
}

function MandateDialog({ offer }) {
  const s = store.get();
  const cur = PERMIT_TIERS[(s.permitTier || 1) - 1];
  const next = PERMIT_TIERS[(s.permitTier || 1)];
  return html`
    <div className="absolute inset-0 flex items-center justify-center pointer-events-auto" style=${{ zIndex: 61, background: 'rgba(10,14,20,0.62)' }}>
      <div className="glass-dark rounded-3xl text-white shadow-2xl" style=${{ width: '480px', maxWidth: '92vw' }}>
        <div style=${{ padding: '18px 20px 14px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
          <div style=${{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style=${{ width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', background: 'linear-gradient(135deg,#f59e0b,#fbbf24)', boxShadow: '0 4px 14px rgba(245,158,11,.4)' }}>🏛️</div>
            <div style=${{ flex: 1, minWidth: 0 }}>
              <div style=${{ fontSize: '16px', fontWeight: 800, letterSpacing: '.01em' }}>Government animal mandate</div>
              <div className="zp-muted" style=${{ fontSize: '12px', marginTop: '2px' }}>${offer.total} adults · permit allows ${offer.cap} · <b style=${{ color: '#fbbf24' }}>${offer.over} over</b></div>
            </div>
          </div>
          <div className="zp-muted" style=${{ fontSize: '13px', marginTop: '11px', lineHeight: 1.5 }}>You're over your permitted number of adult animals. Reduce a species to <b style=${{ color: '#fff' }}>youngsters only</b> — its adults are rehomed for a <b style=${{ color: '#fff' }}>below-market</b> fee (you keep every juvenile) — or raise your permit to keep them all.</div>
        </div>
        <div style=${{ padding: '14px 20px', maxHeight: '42vh', overflowY: 'auto' }}>
          <div style=${{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.14em', color: 'rgba(255,255,255,.4)', fontWeight: 700, marginBottom: '11px' }}>Reduce a species to youngsters</div>
          <div style=${{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            ${offer.species.map((sp) => html`
              <div key=${sp.key} style=${{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <button onClick=${() => bus.emit('mandate:trim', sp.key)} className="zp-listbtn"
                style=${{ background: 'rgba(251,191,36,.13)', borderColor: 'rgba(251,191,36,.4)' }}>
                <span style=${{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.25 }}>
                  <span style=${{ fontWeight: 700 }}>${sp.label}${sp.canRelease ? ' · 🧬 ' + sp.purity + '%' : ''}</span>
                  <span className="zp-muted" style=${{ fontSize: '11px', marginTop: '2px' }}>${sp.adults} adult${sp.adults > 1 ? 's' : ''}${sp.young ? ' · keep ' + sp.young + ' young' : ''}</span>
                </span>
                <span style=${{ textAlign: 'right', lineHeight: 1.25 }}>
                  <span style=${{ display: 'block', fontSize: '13px', fontWeight: 800 }}>+$${sp.earn.toLocaleString('en-US')}</span>
                  <span className="zp-muted" style=${{ display: 'block', fontSize: '11px' }}>rehome ${sp.adults}</span>
                </span>
              </button>
              ${sp.canRelease ? html`<button onClick=${() => bus.emit('animal:release', sp.key)} className="zp-listbtn"
                style=${{ background: 'rgba(52,211,153,.13)', borderColor: 'rgba(52,211,153,.4)' }}>
                <span style=${{ fontWeight: 700 }}>🕊️ Release to the wild</span>
                <span className="zp-muted zp-lb-right" style=${{ fontSize: '11px' }}>grant + prestige, keeps young</span>
              </button>` : ''}
              </div>`)}
          </div>
        </div>
        <div style=${{ padding: '4px 20px 20px' }}>
          ${(cur && cur.upgrade != null && next)
            ? html`<button onClick=${() => bus.emit('permit:upgrade')} disabled=${s.money < cur.upgrade}
                style=${{ width: '100%', padding: '13px', borderRadius: '13px', fontWeight: 800, fontSize: '13px', border: 'none', cursor: s.money >= cur.upgrade ? 'pointer' : 'not-allowed', color: '#fff', background: s.money >= cur.upgrade ? 'linear-gradient(135deg,#0284c7,#38bdf8)' : 'rgba(255,255,255,.08)', opacity: s.money >= cur.upgrade ? 1 : 0.5, boxShadow: s.money >= cur.upgrade ? '0 4px 16px rgba(56,189,248,.35)' : 'none' }}>
                🏛️ Raise permit to Tier ${(s.permitTier || 1) + 1} — $${cur.upgrade.toLocaleString('en-US')} (cap ${next.cap})</button>`
            : html`<div className="zp-muted" style=${{ textAlign: 'center', fontSize: '12px' }}>Permit already at the maximum tier.</div>`}
        </div>
      </div>
    </div>`;
}

function CullDialog({ offer }) {
  const low = offer.diversity < 60;
  return html`
    <div className="absolute inset-0 flex items-center justify-center pointer-events-auto" style=${{ zIndex: 60, background: 'rgba(10,14,20,0.6)' }}>
      <div className="glass-dark rounded-3xl text-white shadow-2xl" style=${{ width: '460px', maxWidth: '92vw' }}>
        <div style=${{ padding: '18px 20px 14px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
          <div style=${{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style=${{ width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', background: 'linear-gradient(135deg,#0e7490,#22d3ee)', boxShadow: '0 4px 14px rgba(34,211,238,.35)' }}>🐾</div>
            <div style=${{ flex: 1, minWidth: 0 }}>
              <div style=${{ fontSize: '16px', fontWeight: 800 }}>${offer.label} — Breeding Herd</div>
              <div className="zp-muted" style=${{ fontSize: '12px', marginTop: '2px' }}>${offer.am}♂ / ${offer.af}♀ adults · young are always kept</div>
            </div>
          </div>
        </div>

        <div style=${{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
          <div className="flex items-center justify-between text-xs font-semibold mb-2">
            <span className="text-white/70">🧬 Bloodline purity</span>
            <span className=${low ? 'text-amber-300' : 'text-emerald-300'}>${offer.diversity}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className=${'h-full rounded-full ' + (low ? 'bg-amber-400' : 'bg-emerald-400')} style=${{ width: offer.diversity + '%' }}></div>
          </div>
          <div className="text-xs text-white/60 mt-2">Offspring resale: <span className="text-white font-semibold">$${offer.offspring.toLocaleString('en-US')}</span>${low ? ' · inbreeding penalty — refresh genetics below' : ''}</div>
        </div>

        <div style=${{ padding: '14px 20px' }}>
          <div style=${{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.14em', color: 'rgba(255,255,255,.4)', fontWeight: 700, marginBottom: '11px' }}>Trim the herd · sell surplus adults</div>
          <div className="flex flex-col gap-2">
            ${offer.options.map((o) => html`
              <button key=${o.label} onClick=${() => bus.emit('cull:choose', { species: offer.species, keepM: o.keepM, keepF: o.keepF })}
                className="zp-listbtn" style=${{ background: 'rgba(52,211,153,.13)', borderColor: 'rgba(52,211,153,.4)' }}>
                <span className="flex flex-col items-start leading-tight">
                  <span className="font-bold">${o.label}</span>
                  <span className="text-[11px] text-white/80 mt-0.5">keep ${o.keepM}♂ / ${o.keepF}♀</span>
                </span>
                <span className="text-right leading-tight">
                  <span className="block text-sm font-bold">+$${o.earned.toLocaleString('en-US')}</span>
                  <span className="block text-[11px] text-white/80 mt-0.5">sell ${o.sell}</span>
                </span>
              </button>`)}
          </div>
        </div>

        <div style=${{ padding: '4px 20px 14px' }}>
          <div style=${{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.14em', color: 'rgba(255,255,255,.4)', fontWeight: 700, marginBottom: '11px' }}>Refresh genetics</div>
          <button onClick=${() => bus.emit('cull:buyblood', offer.species)}
            className="zp-listbtn" style=${{ background: 'rgba(56,189,248,.13)', borderColor: 'rgba(56,189,248,.4)' }}>
            <span className="flex flex-col items-start leading-tight">
              <span className="font-bold">🧬 New blood</span>
              <span className="text-[11px] text-white/80 mt-0.5">sell ALL adults → import 2♂/2♀ fresh · bloodline 100%</span>
            </span>
            <span className="text-sm font-bold shrink-0">${offer.resetNet >= 0 ? '+' : '−'}$${Math.abs(offer.resetNet).toLocaleString('en-US')}</span>
          </button>
        </div>

        <div style=${{ padding: '2px 20px 18px' }}>
          <button onClick=${() => bus.emit('cull:decline', offer.species)}
            className="zp-listbtn" style=${{ justifyContent: 'center', color: 'rgba(255,255,255,.72)', fontWeight: 600 }}>Not now — keep everything</button>
        </div>
      </div>
    </div>`;
}
function App() {
  const s = useGameState();
  if (s.screen === 'menu') return html`<${MainMenu} />`;
  if (s.screen === 'loading') return html`<${LoadingScreen} />`;
  return html`<${HUD} />`;
}

export function mountUI() {
  createRoot(document.getElementById('root')).render(html`<${App} />`);
}
